/**
 * Simple Token Service - Hardcoded rules for domain cards that need tokens
 * No complex parsing or auto-generation - just direct, explicit rules
 *
 * NOTE: New homebrew tokens can be added to TOKEN_DEFINITIONS in mechanicsConfig.js
 * without modifying this file. The config-driven fallback will pick them up automatically.
 */

import { getTokensFromConfig } from '../config/mechanicsConfig';
import { DOMAIN_CARD_CONFIG } from '../config/domainCardConfig';
import { hasClass, hasCommunity, hasAncestry, hasAncestryFeature, hasSubclass, hasSubclassFeature } from '../utils/featureDetection';
import { hasLoadoutCard } from '../utils/cardFeatureDetection';

/**
 * Get token requirements for ancestry features
 * @param {string|Array} ancestryId - Ancestry ID or array of ancestries
 * @param {Object} character - Character data for trait lookups
 * @returns {Array} Array of token/resource definitions
 */
export const getTokensForAncestry = (ancestryId, character) => {
  const tokens = [];

  // Handle ancestry as array (e.g., ['faerie', 'halfling']) or single string
  const ancestries = Array.isArray(ancestryId) ? ancestryId : [ancestryId];

  ancestries.forEach(ancestry => {
    switch (ancestry) {
      case 'faerie':
        tokens.push({
          id: 'luckbender-uses',
          name: 'Luckbender',
          type: 'token',
          current: 1, // Once per session
          maximum: 1,
          refreshTrigger: 'session', // Refreshes at new session
          requiresActivation: true, // Requires Hope to activate
          hopeCost: 3,
          description: 'Spend 3 Hope to reroll Duality Dice for yourself or ally within Close range (once per session)',
          ancestryId: 'faerie' // Track which ancestry this belongs to
        });
        break;

      case 'halfling':
        tokens.push({
          id: 'luckbringer-uses',
          name: 'Luckbringer',
          type: 'token',
          current: 1, // Once per session
          maximum: 1,
          refreshTrigger: 'session', // Refreshes at new session
          requiresActivation: false, // No Hope cost
          description: 'Grant +1 Hope to all party members (including yourself) at session start',
          ancestryId: 'halfling'
        });
        break;

      default:
        // Config-driven fallback for homebrew ancestries
        // Check TOKEN_DEFINITIONS in mechanicsConfig.js for this ancestry
        break;
    }
  });

  // Add any config-driven ancestry tokens not already in the list
  // This allows homebrew ancestries to define tokens without modifying this file
  const configTokens = getTokensFromConfig(character, 'ancestry');
  configTokens.forEach(configToken => {
    // Only add if not already present (avoid duplicates with hardcoded tokens)
    if (!tokens.some(t => t.id === configToken.id)) {
      tokens.push({
        ...configToken,
        ancestryId: character?.ancestry // Add ancestry tracking
      });
    }
  });

  return tokens;
};

/**
 * Get token requirements for community features
 * @param {string} communityId - Community ID
 * @param {Object} character - Character data for trait lookups
 * @returns {Array} Array of token/resource definitions
 */
export const getTokensForCommunity = (communityId, character) => {
  const tokens = [];

  switch (communityId) {
    case 'seaborne':
      tokens.push({
        id: 'tide-tokens',
        name: 'Tide Tokens',
        type: 'token',
        current: 0, // Tokens added when rolling with Fear
        maximum: character.level || 1,
        refreshTrigger: 'session', // Cleared at end of session
        requiresActivation: false,
        description: 'Gain tokens when rolling with Fear, spend for +1 bonus per token to action rolls'
      });
      break;

    case 'orderborne':
      tokens.push({
        id: 'dedicated-uses',
        name: 'Dedicated',
        type: 'token',
        current: 1, // Starts with 1 use per rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest
        requiresActivation: false,
        description: 'Roll d20 as Hope Die when embodying your principles (once per rest)'
      });
      break;

    case 'wanderborne':
      tokens.push({
        id: 'nomadic-pack-uses',
        name: 'Nomadic Pack',
        type: 'token',
        current: 1, // Once per session
        maximum: 1,
        refreshTrigger: 'session', // Refreshes at new session
        requiresActivation: true, // Requires Hope to activate
        hopeCost: 1,
        description: 'Spend 1 Hope to pull a useful mundane item from your pack (once per session)'
      });
      break;

    default:
      // Config-driven fallback for homebrew communities
      break;
  }

  // Add any config-driven community tokens not already in the list
  const configTokens = getTokensFromConfig(character, 'community');
  configTokens.forEach(configToken => {
    if (!tokens.some(t => t.id === configToken.id)) {
      tokens.push(configToken);
    }
  });

  return tokens;
};

/**
 * Get token requirements for class features
 * @param {string} classId - Class ID
 * @param {Object} character - Character data for level lookups
 * @returns {Array} Array of token/resource definitions
 */
export const getTokensForClass = (classId, character) => {
  const tokens = [];
  const normalizedClassId = classId?.toLowerCase();

  switch (normalizedClassId) {
    case 'bard':
      // Rally Dice: d6 at L1, d8 at L5, d10 with Epic Poetry (Wordsmith Mastery)
      // Epic Poetry is a Wordsmith mastery feature that upgrades Rally Die to d10
      const hasEpicPoetry = hasSubclassFeature(character, 'Epic Poetry');
      const rallyDieSize = hasEpicPoetry ? 10 : (character.level >= 5) ? 8 : 6;
      tokens.push({
        id: 'rally-die',
        name: 'Rally Die',
        type: 'dice',
        current: 0, // Created when bard uses Rally ability
        maximum: 1, // Only one Rally Die per session
        dieSize: rallyDieSize,
        refreshTrigger: 'session', // Cleared at end of session
        requiresActivation: false,
        description: `d${rallyDieSize} that can be spent to add to action roll, reaction roll, damage roll, or clear Stress equal to result (once per session)`
      });
      break;

    case 'sorcerer':
      // Channel Raw Power: Once per session, sacrifice domain card for Hope or damage bonus
      tokens.push({
        id: 'channel-raw-power',
        name: 'Channel Raw Power',
        type: 'token',
        current: 1, // 1 = available, 0 = used
        maximum: 1,
        refreshTrigger: 'session',
        requiresActivation: false,
        description: 'Place domain card from loadout into vault. Gain Hope equal to card level OR damage bonus equal to 2× card level on next spell.'
      });
      break;

    case 'guardian':
      // Unstoppable: d4 at L1, d6 at L5 - value tracks current die value (0 = inactive)
      const unstoppableDieSize = (character.level >= 5) ? 6 : 4;
      tokens.push({
        id: 'unstoppable-die',
        name: 'Unstoppable Die',
        type: 'dice',
        current: 0, // 0 = inactive, 1-max = active with that value
        maximum: unstoppableDieSize,
        dieSize: unstoppableDieSize,
        refreshTrigger: 'long-rest', // Once per long rest
        requiresActivation: true, // Must activate to start
        description: `d${unstoppableDieSize} - Activate to become Unstoppable. Die value starts at 1, increases when you deal damage. Adds to damage, reduces damage severity, immune to Restrained/Vulnerable.`
      });
      break;

    default:
      // Config-driven fallback for homebrew classes
      break;
  }

  // Add any config-driven class tokens not already in the list
  const configTokens = getTokensFromConfig(character, 'class');
  configTokens.forEach(configToken => {
    if (!tokens.some(t => t.id === configToken.id)) {
      tokens.push(configToken);
    }
  });

  return tokens;
};

/**
 * Get token requirements for subclass features
 * @param {string} subclassId - Subclass ID
 * @param {Object} character - Character data for proficiency lookups
 * @returns {Array} Array of token/resource definitions
 */
export const getTokensForSubclass = (subclassId, character) => {
  const tokens = [];

  switch (subclassId) {
    case 'call-of-the-slayer': {
      // Slayer Dice: Can store up to Proficiency number of d6 dice
      const proficiency = character.proficiency || 2; // Default to 2 if not set
      tokens.push({
        id: 'slayer-dice',
        name: 'Slayer Dice',
        type: 'dice',
        current: 0, // Dice added when rolling with Hope
        maximum: proficiency, // Max = Proficiency
        dieSize: 6, // Always d6
        refreshTrigger: 'session', // Cleared at end of session
        requiresActivation: false,
        sessionRewardPerDie: 1, // Gain 1 Hope per unspent die cleared at session end
        description: `Store up to ${proficiency} d6 dice. On Hope rolls, choose to add a die instead of gaining Hope. Spend dice on attack or damage rolls. Session end: clear unspent dice and gain Hope per die cleared.`,
        subclassId: 'call-of-the-slayer' // Track which subclass this belongs to
      });
      // Weapon Specialist (Specialization): Reroll 1s on Slayer Dice once per rest
      tokens.push({
        id: 'reroll-ones',
        name: 'Reroll Ones',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'rest',
        subclassId: 'call-of-the-slayer',
        description: 'Once per rest: reroll 1s on Slayer Dice (Weapon Specialist)'
      });
      break;
    }

    // -------------------------------------------------------------------------
    // Phase B Subclasses (Jan 2026)
    // -------------------------------------------------------------------------

    case 'beastbound':
      // Loyal Friend (Mastery): Damage swap with companion once per long rest
      tokens.push({
        id: 'loyal-friend',
        name: 'Loyal Friend',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'beastbound',
        description: 'Swap damage with companion when at last HP/Stress (once per long rest)'
      });
      break;

    case 'troubadour': {
      // 3 songs, 1/rest each (2/rest with Virtuoso mastery)
      const hasVirtuoso = hasSubclassFeature(character, 'Virtuoso');
      const songsPerRest = hasVirtuoso ? 2 : 1;
      ['relaxing', 'epic', 'heartbreaking'].forEach(song => {
        tokens.push({
          id: `${song}-song`,
          name: `${song.charAt(0).toUpperCase() + song.slice(1)} Song`,
          type: 'token',
          current: songsPerRest,
          maximum: songsPerRest,
          refreshTrigger: 'long-rest',
          subclassId: 'troubadour'
        });
      });
      break;
    }

    case 'wordsmith':
      tokens.push({
        id: 'rousing-speech',
        name: 'Rousing Speech',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'wordsmith'
      });
      tokens.push({
        id: 'eloquent-uses',
        name: 'Eloquent',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'session',
        subclassId: 'wordsmith'
      });
      break;

    case 'syndicate': {
      // 1/session normally, 3/session with Reliable Backup mastery
      const hasReliableBackup = hasSubclassFeature(character, 'Reliable Backup');
      const contactsMax = hasReliableBackup ? 3 : 1;
      tokens.push({
        id: 'contacts-uses',
        name: 'Contacts',
        type: 'token',
        current: contactsMax,
        maximum: contactsMax,
        refreshTrigger: 'session',
        subclassId: 'syndicate'
      });
      break;
    }

    case 'divine-wielder': {
      // 1/rest normally, 2/rest with Devout specialization
      const hasDevout = hasSubclassFeature(character, 'Devout');
      const sparingMax = hasDevout ? 2 : 1;
      tokens.push({
        id: 'sparing-touch',
        name: 'Sparing Touch',
        type: 'token',
        current: sparingMax,
        maximum: sparingMax,
        refreshTrigger: 'long-rest',
        subclassId: 'divine-wielder'
      });
      break;
    }

    case 'call-of-the-brave':
      tokens.push({
        id: 'battle-ritual',
        name: 'Battle Ritual',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'call-of-the-brave'
      });
      // Camaraderie (Mastery): Extra Tag Team Roll per session
      tokens.push({
        id: 'extra-tag-team',
        name: 'Extra Tag Team',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'session',
        subclassId: 'call-of-the-brave',
        description: '+1 Tag Team/session, allies pay 2 Hope (Camaraderie)'
      });
      break;

    case 'warden-of-renewal':
      tokens.push({
        id: 'clarity-of-nature',
        name: 'Clarity of Nature',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'warden-of-renewal'
      });
      tokens.push({
        id: 'wardens-protection',
        name: "Warden's Protection",
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'warden-of-renewal'
      });
      break;

    case 'warden-of-the-elements':
      tokens.push({
        id: 'elemental-aura',
        name: 'Elemental Aura',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        subclassId: 'warden-of-the-elements'
      });
      break;

    default:
      // Config-driven fallback for homebrew subclasses
      break;
  }

  // Add any config-driven subclass tokens not already in the list
  const configTokens = getTokensFromConfig(character, 'subclass');
  configTokens.forEach(configToken => {
    if (!tokens.some(t => t.id === configToken.id)) {
      tokens.push({
        ...configToken,
        subclassId // Add subclass tracking
      });
    }
  });

  return tokens;
};

/**
 * Get tokens from domain card config (domainCardConfig.js tokenConfig property)
 * This is the primary config-driven approach for domain card tokens.
 * @param {string} cardId - Domain card ID
 * @param {Object} character - Character data for trait lookups
 * @returns {Array} Array of token definitions
 */
const getTokensFromDomainCardConfig = (cardId, character) => {
  const tokens = [];
  const cardConfig = DOMAIN_CARD_CONFIG[cardId];
  if (!cardConfig?.tokenConfig) return tokens;

  const configs = Array.isArray(cardConfig.tokenConfig)
    ? cardConfig.tokenConfig
    : [cardConfig.tokenConfig];

  for (const config of configs) {
    const max = typeof config.getMax === 'function'
      ? config.getMax(character)
      : (config.max || 1);

    tokens.push({
      id: config.id,
      name: config.name || config.id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      type: config.type || 'token',
      current: max,
      maximum: max,
      refreshTrigger: config.refreshOn || 'session',
      ...(config.requiresActivation && { requiresActivation: true }),
      ...(config.die && { dieSize: parseInt(config.die.replace('d', '')) }),
      ...(config.cost?.type === 'hope' && { hopeCost: config.cost.amount }),
      ...(config.cost?.type === 'stress' && { stressCost: config.cost.amount }),
      ...(config.description && { description: config.description }),
    });
  }
  return tokens;
};

/**
 * Get token requirements for specific domain cards
 * @param {string} cardId - Domain card ID
 * @param {Object} character - Character data for trait lookups
 * @param {string} spellcastTrait - Optional spellcast trait name for accurate lookups
 * @returns {Array} Array of token/resource definitions
 */
export const getTokensForCard = (cardId, character, spellcastTrait = null) => {
  const tokens = [];

  // =========================================================================
  // PRIMARY PATH: Check domainCardConfig.js tokenConfig FIRST
  // If config exists, use it exclusively - skip the legacy switch statement
  // =========================================================================
  const domainConfigTokens = getTokensFromDomainCardConfig(cardId, character);
  if (domainConfigTokens.length > 0) {
    // Config found - use it and skip the switch
    tokens.push(...domainConfigTokens);

    // Also check secondary config (mechanicsConfig.js) for additional tokens
    const charForLookup = { loadout: [cardId] };
    const mechanicsConfigTokens = getTokensFromConfig(charForLookup, 'domain-card');
    mechanicsConfigTokens.forEach(configToken => {
      if (!tokens.some(t => t.id === configToken.id)) {
        tokens.push(configToken);
      }
    });

    return tokens;
  }

  // =========================================================================
  // LEGACY FALLBACK: No config found - use switch statement
  // TODO: Migrate all switch cases to tokenConfig in domainCardConfig.js
  // =========================================================================
  switch (cardId) {
    case 'unleash-chaos':
      // Get spellcast trait value - requires spellcastTrait param from caller (e.g., from allData.subclasses)
      // Fallback to 2 if not provided
      let spellcastValue = 2;

      if (spellcastTrait && character.traits?.[spellcastTrait]) {
        spellcastValue = character.traits[spellcastTrait];
      }

      // Ensure minimum of 1 token
      spellcastValue = Math.max(1, spellcastValue);
      tokens.push({
        id: 'chaos-tokens',
        name: 'Chaos Tokens',
        type: 'token',
        current: spellcastValue,
        maximum: spellcastValue,
        refreshTrigger: 'session'
      });
      break;

    case 'strategic-approach':
    case 'core_domain_card_strategic_approach':
      tokens.push({
        id: 'strategy-tokens',
        name: 'Strategy Tokens',
        type: 'token',
        current: Math.max(1, character.traits?.Knowledge || 1),
        maximum: Math.max(1, character.traits?.Knowledge || 1),
        refreshTrigger: 'long-rest'
      });
      break;

    case 'inspirational-words':
    case 'core_domain_card_inspirational_words':
      const presenceValue = character.traits?.Presence || 1;
      tokens.push({
        id: 'inspiration-tokens',
        name: 'Inspiration Tokens',
        type: 'token',
        current: presenceValue,
        maximum: presenceValue,
        refreshTrigger: 'long-rest', // Refresh at long rest per card text
        requiresActivation: false,
        allowManualSpend: true, // Allow manual spending for ally support
        description: 'Spend tokens when speaking with allies to clear their Stress/Hit Points or give Hope'
      });
      break;

    case 'restoration':
    case 'core_domain_card_restoration':
      // Determine spellcast trait value
      let restorationSpellcastValue = 2; // Fallback
      if (spellcastTrait && character.traits?.[spellcastTrait]) {
        restorationSpellcastValue = character.traits[spellcastTrait];
      } else {
        // Try common spellcast traits (both cases)
        restorationSpellcastValue =
          character.traits?.Presence || character.traits?.presence ||
          character.traits?.Instinct || character.traits?.instinct ||
          character.traits?.Knowledge || character.traits?.knowledge || 2;
      }

      tokens.push({
        id: 'restoration-tokens',
        name: 'Restoration Tokens',
        type: 'token',
        current: restorationSpellcastValue,
        maximum: restorationSpellcastValue,
        refreshTrigger: 'long-rest'
      });
      break;

    case 'invisibility':
      // Determine spellcast trait value
      let invisibilitySpellcastValue = 2; // Fallback
      if (spellcastTrait && character.traits?.[spellcastTrait]) {
        invisibilitySpellcastValue = character.traits[spellcastTrait];
      } else {
        // Try common spellcast traits (both cases)
        invisibilitySpellcastValue =
          character.traits?.Presence || character.traits?.presence ||
          character.traits?.Instinct || character.traits?.instinct ||
          character.traits?.Knowledge || character.traits?.knowledge || 2;
      }

      tokens.push({
        id: 'invisibility-tokens',
        name: 'Invisibility Tokens',
        type: 'token',
        current: 0, // Must be activated via Spellcast Roll first
        maximum: invisibilitySpellcastValue,
        refreshTrigger: 'manual', // Activated via spell, not automatic refresh
        requiresActivation: true,
        activationDC: 10, // Spellcast Roll (10) required
        activationText: 'Make Spellcast Roll (10) to activate and place tokens equal to your Spellcast trait',
        description: 'Target becomes Invisible. Spend tokens when taking actions while invisible.'
      });
      break;

    case 'uncanny-disguise':
      tokens.push({
        id: 'disguise-tokens',
        name: 'Disguise Tokens',
        type: 'token',
        current: 0, // Must be activated by marking Stress
        maximum: character.traits?.Presence || character.traits?.Instinct || character.traits?.Knowledge || 2,
        refreshTrigger: 'manual', // Activated by marking stress, not automatic refresh
        requiresActivation: true,
        activationText: 'Mark a Stress to activate and place tokens'
      });
      break;

    case 'never-upstaged':
      tokens.push({
        id: 'upstage-tokens',
        name: 'Upstage Tokens',
        type: 'token',
        current: 0, // Tokens added when taking damage
        maximum: null, // No maximum - accumulates based on damage taken
        refreshTrigger: 'on-use', // Cleared when used for attack
        requiresActivation: false,
        requiresVitalsIntegration: true, // Special HP tracking integration
        description: 'Gain tokens equal to HP marked when taking damage, spend all on next attack for +5 damage per token'
      });
      break;

    case 'spellcharge':
      // Get spellcast trait value properly
      let spellchargeSpellcastValue = 2; // Fallback
      if (spellcastTrait && character.traits?.[spellcastTrait]) {
        spellchargeSpellcastValue = character.traits[spellcastTrait];
      } else {
        // Try all possible spellcast traits as fallback
        spellchargeSpellcastValue = Math.max(
          character.traits?.Presence || 0,
          character.traits?.Instinct || 0,
          character.traits?.Knowledge || 0,
          character.traits?.Finesse || 0,
          2 // minimum fallback
        );
      }
      tokens.push({
        id: 'charge-tokens',
        name: 'Charge Tokens',
        type: 'token',
        current: 0, // Tokens added when taking magic damage
        maximum: spellchargeSpellcastValue, // "store a number of tokens equal to your Spellcast trait"
        refreshTrigger: 'manual', // Tokens persist until spent
        requiresActivation: false,
        description: 'Gain tokens when taking magic damage, spend for +d6 damage per token'
      });
      break;

    case 'thorn-skin':
      tokens.push({
        id: 'thorn-tokens',
        name: 'Thorn Tokens',
        type: 'token',
        current: 0, // Must be activated by spending Hope
        maximum: character.traits?.Presence || character.traits?.Instinct || character.traits?.Knowledge || 2,
        refreshTrigger: 'rest', // SRD: "Once per rest... When you take a rest, clear all unspent tokens"
        requiresActivation: true,
        activationText: 'Spend a Hope to activate and place tokens'
      });
      break;

    case 'twilight-toll':
      tokens.push({
        id: 'toll-tokens',
        name: 'Toll Tokens',
        type: 'token',
        current: 0, // Tokens added on successful non-damage rolls
        maximum: 99, // No real maximum, can accumulate
        refreshTrigger: 'target-change', // Cleared when changing targets or resting
        requiresActivation: false,
        description: 'Gain tokens on non-damage successes, spend for +d12 damage per token against target'
      });
      break;

    case 'wild-fortress':
      // Only return the token template if it hasn't been activated yet
      // Once activated, it will be stored directly in cardResources with requiresActivation: false
      const existingFortress = character.cardResources?.['wild-fortress']?.['fortress-hp'];
      if (!existingFortress || existingFortress.requiresActivation === true) {
        tokens.push({
          id: 'fortress-hp',
          name: 'Fortress Hit Points',
          type: 'token',
          current: 0, // Represents damage taken (0-3)
          maximum: 3, // Dome destroyed at 3 HP
          refreshTrigger: 'manual', // Manual tracking of dome HP
          requiresActivation: true,
          activationText: 'Make Spellcast Roll (13) + spend 2 Hope to create dome'
        });
      }
      break;

    case 'fane-of-the-wilds':
      // Per card text: "Place tokens on this card equal to the number of Sage domain cards you have equipped."
      // For now, default to 3 tokens (can be adjusted based on actual Sage cards)
      // TODO: Implement proper Sage domain card detection
      const faneTokenCount = 3;

      tokens.push({
        id: 'fane-tokens',
        name: 'Fane Tokens',
        type: 'token',
        current: faneTokenCount,
        maximum: faneTokenCount,
        refreshTrigger: 'long-rest',
        requiresActivation: false,
        description: 'Spend for +1 to Spellcast rolls, gain token on Sage spell crits'
      });
      break;

    case 'flight':
      tokens.push({
        id: 'flight-tokens',
        name: 'Flight Tokens',
        type: 'token',
        current: 0, // Must be activated via Spellcast Roll first
        maximum: character.traits?.Agility || 1,
        refreshTrigger: 'manual', // Activated via spell, not automatic refresh
        requiresActivation: true,
        activationText: 'Make Spellcast Roll (15) to activate and place tokens'
      });
      break;

    // Rune Ward: Personal trinket infused with protective magic
    // Ward holder can spend Hope to reduce incoming damage by 1d8
    // If d8 result is 8, ward depletes and must be recharged on rest
    case 'rune-ward':
    case 'core_domain_card_rune_ward':
      tokens.push({
        id: 'ward-charge',
        name: 'Rune Ward',
        type: 'token',
        current: 1, // 1 = charged (ready to use), 0 = depleted (needs rest)
        maximum: 1,
        refreshTrigger: 'rest', // Recharges on any rest (short or long)
        requiresActivation: true,
        hopeCost: 1,
        activationText: 'Spend 1 Hope to reduce incoming damage by 1d8',
        description: 'A personal trinket infused with protective magic. Spend Hope to reduce damage by 1d8. If you roll an 8, the ward depletes until your next rest.',
        wardDieSize: 8, // d8 for damage reduction
        breakOnMax: true // Ward breaks (depletes) when max value is rolled
      });
      break;

    // Mending Touch (Splendor L1): Healing spell with enhanced mode once per long rest
    // Normal: Spend 2 Hope to clear 1 HP or 1 Stress on target
    // Enhanced (once per long rest): Clear 2 HP or 2 Stress instead of 1 (still costs 2 Hope)
    case 'mending-touch':
    case 'core_domain_card_mending_touch':
      tokens.push({
        id: 'mending-touch-enhanced',
        name: 'Mending Touch (Enhanced)',
        type: 'token',
        current: 1, // 1 = enhanced use available, 0 = used this long rest
        maximum: 1,
        refreshTrigger: 'long-rest',
        requiresActivation: true,
        hopeCost: 2,
        activationText: 'When spending healing time learning about the target or revealing something about yourself, clear 2 HP or 2 Stress instead of 1',
        description: 'Lay hands upon a creature to channel healing magic. Spend 2 Hope to clear 1 HP or 1 Stress. Once per long rest, when you spend healing time learning something new about them or revealing something about yourself, clear 2 HP or 2 Stress instead.',
        healingModes: {
          normal: { hopeCost: 2, healAmount: 1 },
          enhanced: { hopeCost: 2, healAmount: 2, usesToken: true }
        }
      });
      break;

    // Deft Maneuvers (Bone L1): Sprint without Agility Roll, +1 attack if ending in melee
    // Once per rest, mark a Stress to sprint anywhere within Far range without making an Agility Roll.
    // If you end this movement within Melee range of an adversary and immediately attack, gain +1 to attack roll.
    case 'deft-maneuvers':
    case 'core_domain_card_deft_maneuvers':
      tokens.push({
        id: 'deft-maneuvers-uses',
        name: 'Deft Maneuvers',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        stressCost: 1,
        activationText: 'Mark a Stress to sprint anywhere within Far range without an Agility Roll',
        description: 'Once per rest, mark a Stress to sprint anywhere within Far range without making an Agility Roll. If you end within Melee range of an adversary and immediately attack, gain +1 to the attack roll.',
        conditionalBonus: {
          condition: 'End movement in Melee range and attack immediately',
          bonus: '+1 to attack roll'
        }
      });
      break;

    // Healing Field (Sage L4): AoE healing field with enhanced mode
    // Base: Once per long rest, you and all allies in Close range clear 1 HP
    // Enhanced: Spend 2 Hope to clear 2 HP instead of 1
    case 'healing-field':
    case 'core_domain_card_healing_field':
      tokens.push({
        id: 'healing-field-uses',
        name: 'Healing Field',
        type: 'token',
        current: 1, // 1 = available, 0 = used this long rest
        maximum: 1,
        refreshTrigger: 'long-rest',
        requiresActivation: true,
        activationText: 'Conjure a field of healing plants around you (Close range)',
        description: 'Once per long rest, conjure a field of healing plants. You and all allies within Close range clear 1 HP. Spend 2 Hope to clear 2 HP instead.',
        healingModes: {
          normal: { hopeCost: 0, healAmount: 1, targetCount: 'self+allies', range: 'Close' },
          enhanced: { hopeCost: 2, healAmount: 2, targetCount: 'self+allies', range: 'Close' }
        },
        aoeHealing: true // Flag for UI to know this affects multiple targets
      });
      break;

    // Towering Stalk (Sage L3): Dual-mode spell
    // Utility mode (once per rest): Conjure climbable stalk to Far range height
    // Attack mode (unlimited, costs Stress): Spellcast Roll, d8 physical damage vs Close range targets
    case 'towering-stalk':
    case 'core_domain_card_towering_stalk':
      tokens.push({
        id: 'stalk-utility-uses',
        name: 'Towering Stalk (Utility)',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: false, // Click to use, no roll required for utility mode
        description: 'Conjure a thick, twisting stalk within Close range that can be easily climbed. Its height can grow up to Far range.',
        modes: {
          utility: {
            name: 'Conjure Stalk',
            cost: 'none',
            usesToken: true,
            range: 'Close (conjure), Far (height)',
            effect: 'Create climbable stalk'
          },
          attack: {
            name: 'Erupting Stalk',
            stressCost: 1,
            usesToken: false, // Attack mode does NOT consume the once-per-rest use
            range: 'Close',
            effect: 'Spellcast Roll vs targets, d8 physical damage with Proficiency',
            damageFormula: 'd8',
            damageType: 'physical'
          }
        }
      });
      break;

    // Forest Sprites (Sage L8): Summon protective sprites that aid allies
    // Spellcast Roll (13) + spend X Hope to create X sprites
    // Sprites grant: +3 attack bonus to allies attacking nearby enemies,
    //                Extra Armor Slot when ally marks armor near sprite
    // Sprites vanish after granting one benefit or taking any damage
    case 'forest-sprites':
    case 'core_domain_card_forest_sprites':
      tokens.push({
        id: 'active-sprites',
        name: 'Forest Sprites',
        type: 'token',
        current: 0, // Sprites created via Spellcast Roll + Hope spending
        maximum: 99, // No real maximum - limited only by Hope available
        refreshTrigger: 'manual', // Sprites persist until consumed/destroyed
        requiresActivation: true,
        activationDC: 13, // Spellcast Roll (13) required
        activationText: 'Make Spellcast Roll (13), then spend Hope to create sprites (1 Hope = 1 sprite)',
        description: 'Small forest sprites appear at points within Far range. Allies attacking adversaries in Melee range of a sprite gain +3 to attack. Allies marking Armor Slots in Melee range of a sprite mark an additional slot. Sprites vanish after granting a benefit or taking any damage.',
        benefitsProvided: {
          attackBonus: {
            value: 3,
            condition: 'Ally attacks adversary within Melee range of a sprite'
          },
          armorBonus: {
            value: 1,
            condition: 'Ally marks Armor Slot while within Melee range of a sprite'
          }
        },
        consumptionTriggers: [
          'Sprite grants attack bonus',
          'Sprite grants armor bonus',
          'Sprite takes any damage'
        ],
        range: 'Far',
        spellLevel: 8,
        recallCost: 2
      });
      break;

    // Second Wind (Splendor L3): Once per rest, after successful attack, clear 3 Stress or 1 HP
    // On success with Hope: Also affect an ally within Close range
    case 'second-wind':
    case 'core_domain_card_second_wind':
      tokens.push({
        id: 'second-wind-uses',
        name: 'Second Wind',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        activationText: 'Use after succeeding on an attack against an adversary',
        description: 'Once per rest, when you succeed on an attack against an adversary, you can clear 3 Stress or a Hit Point. On a success with Hope, you also clear 3 Stress or a Hit Point on an ally within Close range of you.',
        healingOptions: {
          self: {
            stressAmount: 3,
            hpAmount: 1,
            choice: true // Player chooses Stress OR HP
          },
          ally: {
            stressAmount: 3,
            hpAmount: 1,
            choice: true, // Player chooses Stress OR HP for ally
            condition: 'Success with Hope',
            range: 'Close'
          }
        },
        triggerCondition: 'attack_success' // Triggers after successful attack
      });
      break;

    // Signature Move (Bone L5): Name a signature combat move, roll d20 as Hope Die
    // Once per rest, when performing signature move, roll d20 as Hope Die
    // On success, clear 1 Stress
    case 'signature-move':
    case 'core_domain_card_signature_move':
      tokens.push({
        id: 'signature-move-uses',
        name: 'Signature Move',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        activationText: 'Use during your signature combat move to roll d20 as Hope Die',
        description: 'Name and describe your signature combat move. Once per rest, when you perform this signature move as part of an action, roll a d20 as your Hope Die. On a success, clear a Stress.',
        hopeDieOverride: 'd20', // Override Hope die to d20
        onSuccessEffect: {
          type: 'clear_stress',
          amount: 1,
          description: 'Clear 1 Stress on success'
        }
      });
      break;

    // Midnight-Touched (Midnight L7): Conditional ability - requires 4+ Midnight cards in loadout
    // Benefits: Once per rest gain Hope when at 0 Hope and GM would gain Fear
    //           On successful attack, mark Stress to add Fear Die result to damage
    case 'midnight-touched':
    case 'core_domain_card_midnight_touched':
      // Count Midnight domain cards in loadout
      // Note: This requires loadout card data to be available for proper domain checking
      // For now, we create tokens unconditionally - UI should check activation condition
      tokens.push({
        id: 'midnight-hope-steal',
        name: 'Midnight-Touched (Hope Steal)',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        activationText: 'When you have 0 Hope and GM would gain a Fear, gain a Hope instead',
        description: 'When 4+ Midnight domain cards are in your loadout: Once per rest, when you have 0 Hope and the GM would gain a Fear, you can gain a Hope instead.',
        conditionalActivation: {
          type: 'domain-count',
          domain: 'MIDNIGHT',
          minimumCards: 4,
          source: 'loadout'
        },
        triggerCondition: {
          hopeRequired: 0,
          gmGainsFear: true
        }
      });
      // Second ability: Add Fear Die to damage on successful attack (costs Stress, unlimited uses)
      tokens.push({
        id: 'midnight-fear-damage',
        name: 'Midnight-Touched (Fear Damage)',
        type: 'token',
        current: 99, // Unlimited uses (only limited by Stress)
        maximum: 99,
        refreshTrigger: 'manual', // No refresh needed - unlimited use
        requiresActivation: true,
        stressCost: 1,
        activationText: 'After a successful attack, mark a Stress to add Fear Die result to damage',
        description: 'When 4+ Midnight domain cards are in your loadout: When you make a successful attack, you can mark a Stress to add the result of your Fear Die to your damage roll.',
        conditionalActivation: {
          type: 'domain-count',
          domain: 'MIDNIGHT',
          minimumCards: 4,
          source: 'loadout'
        },
        triggerCondition: 'attack_success',
        damageBonus: {
          type: 'fear-die-result',
          description: 'Add Fear Die result to damage'
        }
      });
      break;

    // Arcana-Touched (Arcana L7): Conditional ability - requires 4+ Arcana cards in loadout
    // Benefits: +1 Spellcast Rolls (passive), Once per rest switch Hope/Fear dice results
    case 'arcana-touched':
    case 'core_domain_card_arcana_touched':
      // Count Arcana domain cards in loadout
      // Note: This requires loadout card data to be available for proper domain checking
      // For now, we create the token unconditionally - UI should check activation condition
      tokens.push({
        id: 'arcana-touched-switch',
        name: 'Arcana-Touched (Dice Switch)',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        activationText: 'Switch the results of your Hope and Fear Dice after a roll',
        description: 'When 4+ Arcana domain cards are in your loadout: +1 to Spellcast Rolls (passive). Once per rest, switch the results of your Hope and Fear Dice.',
        conditionalActivation: {
          type: 'domain-count',
          domain: 'ARCANA',
          minimumCards: 4,
          source: 'loadout',
          passiveBonus: {
            type: 'spellcast-modifier',
            value: 1,
            description: '+1 to Spellcast Rolls'
          }
        }
      });
      break;

    // Sage-Touched (Sage L7): Conditional ability - requires 4+ Sage cards in loadout
    // Benefits: +2 Spellcast in natural environments (passive), Once per rest double Agility or Instinct
    case 'sage-touched':
    case 'core_domain_card_sage_touched':
      // Create the token unconditionally - UI should check activation condition via checkSageTouchedActive()
      tokens.push({
        id: 'sage-touched-double-trait',
        name: 'Sage-Touched (Double Trait)',
        type: 'token',
        current: 1, // 1 = available, 0 = used this rest
        maximum: 1,
        refreshTrigger: 'rest', // Refreshes on any rest (short or long)
        requiresActivation: true,
        activationText: 'Double your Agility or Instinct for a roll (choose before rolling)',
        description: 'When 4+ Sage domain cards are in your loadout: +2 to Spellcast Rolls in natural environments. Once per rest, double your Agility or Instinct when making a roll.',
        conditionalActivation: {
          type: 'domain-count',
          domain: 'SAGE',
          minimumCards: 4,
          source: 'loadout',
          passiveBonus: {
            type: 'spellcast-modifier',
            value: 2,
            condition: 'natural-environment',
            description: '+2 to Spellcast Rolls while in a natural environment'
          }
        },
        traitDoubleOptions: ['Agility', 'Instinct'] // Player must choose one before rolling
      });
      break;

    // Wild Surge (Sage L7): Progressive die that enhances all action rolls
    // Once per long rest, mark Stress to channel nature. Place d6 at value 1.
    // Add die value to every action roll. After adding, increase by 1.
    // Ends when die would exceed 6 or on rest (mark extra Stress).
    case 'wild-surge':
    case 'core_domain_card_wild_surge':
      tokens.push({
        id: 'wild-surge-die',
        name: 'Wild Surge Die',
        type: 'progressive-die',
        current: 0, // 0 = inactive, 1-6 = active die value
        maximum: 6,
        dieSize: 6,
        refreshTrigger: 'long-rest',
        requiresActivation: true,
        stressCost: 1, // Mark Stress to activate
        activationText: 'Mark a Stress to channel nature and enhance yourself',
        description: 'Add die value to every action roll. After each roll, die increases by 1. Ends at 6 or on rest (mark extra Stress).',
        progressionTrigger: 'after-action-roll', // Increases after each action roll
        endConditions: ['die-exceeds-6', 'rest'],
        endCost: { type: 'stress', amount: 1 }, // Mark extra Stress when ending
        flavorText: 'Describe how your appearance changes as you channel the natural world.'
      });
      break;

    // Zone of Protection (Splendor L6): Progressive die that reduces ally damage
    // Spellcast (16), once per long rest. Creates zone at Far range.
    // Allies in Very Close range of zone reduce damage by die value.
    // After reducing, increase by 1. Ends when die would exceed 6.
    case 'zone-of-protection':
    case 'core_domain_card_zone_of_protection':
      tokens.push({
        id: 'protection-die',
        name: 'Zone Protection Die',
        type: 'progressive-die',
        current: 0, // 0 = inactive, 1-6 = active die value
        maximum: 6,
        dieSize: 6,
        refreshTrigger: 'long-rest',
        requiresActivation: true,
        activationDC: 16, // Spellcast Roll (16) required
        activationText: 'Make Spellcast Roll (16) to create protective zone within Far range',
        description: 'Allies in Very Close range of zone reduce damage by die value. After reducing, die increases by 1. Ends at 6.',
        progressionTrigger: 'after-damage-reduction',
        endConditions: ['die-exceeds-6'],
        range: { create: 'Far', effect: 'Very Close' }
      });
      break;

    // Confusing Aura (Arcana L8): Layer-based defense with Spellcast activation
    // Spellcast (14) to create 1 layer. Mark Stress for additional layers.
    // When attacked, roll d6s equal to layers. 5+ destroys layer, attack fails.
    // All results 4 or lower = damage and spell ends. Once per long rest.
    case 'confusing-aura':
    case 'core_domain_card_confusing_aura':
      tokens.push({
        id: 'aura-layers',
        name: 'Aura Layers',
        type: 'layer-defense', // Must match for long-rest refresh
        current: 0, // Layers created via activation
        maximum: 10, // Reasonable max for display (actual limit is Stress)
        refreshTrigger: 'long-rest',
        requiresActivation: true,
        description: 'Spellcast (14) for 1 layer. +1 Stress per extra. Roll d6 per layer when attacked.'
      });
      break;

    // Sigil of Retribution (Codex L6): Damage accumulator against marked target
    // Mark adversary within Close range (GM gains Fear).
    // Gain d8 when marked target damages allies. Max d8s = character level.
    // On successful attack, add all accumulated d8s to damage, then clear.
    case 'sigil-of-retribution':
    case 'core_domain_card_sigil_of_retribution':
      tokens.push({
        id: 'retribution-dice',
        name: 'Retribution Dice',
        type: 'token',
        current: 0, // Accumulated d8s
        maximum: character.level || 5, // Max = character level
        refreshTrigger: 'manual', // Clears on target defeat or recast
        requiresActivation: true,
        activationText: 'Mark an adversary within Close range (GM gains Fear)',
        description: 'Gain d8 when marked target damages allies. On successful attack, roll all d8s for extra damage.',
        accumulationTrigger: 'marked-target-damages-ally',
        consumptionTrigger: 'attack-success-vs-marked',
        damageBonus: { dieSize: 8, perToken: true },
        range: 'Close',
        gmFearOnActivate: true,
        endConditions: ['marked-target-defeated', 'recast']
      });
      break;

    // Mass Disguise (Midnight L6): Countdown-based group disguise
    // Mark Stress to change appearance of all willing creatures in Close range.
    // Countdown (8) ticks as GM consequence. When triggers, disguise drops.
    case 'mass-disguise':
    case 'core_domain_card_mass_disguise':
      tokens.push({
        id: 'disguise-countdown',
        name: 'Disguise Countdown',
        type: 'countdown',
        current: 0, // 0 = inactive, 8 = just activated, ticks down
        maximum: 8,
        refreshTrigger: 'manual',
        requiresActivation: true,
        stressCost: 1,
        activationTime: 'few-minutes', // Requires setup time
        activationText: 'Mark a Stress to change appearance of all willing creatures within Close range',
        description: 'Disguised creatures have advantage on Presence Rolls to avoid scrutiny. Countdown ticks as GM consequence.',
        tickTrigger: 'gm-consequence',
        tickAmount: -1, // Decreases
        endCondition: 'reaches-zero',
        range: 'Close',
        benefitsProvided: {
          advantage: {
            on: 'Presence Rolls',
            condition: 'Avoiding scrutiny'
          }
        }
      });
      break;

    // Frenzy (Blade L8): Once per long rest transformation
    // Enter Frenzy until no adversaries in sight. +10 damage, +8 Severe threshold, no Armor Slots.
    case 'frenzy':
    case 'core_domain_card_frenzy':
      tokens.push({
        id: 'frenzy-uses',
        name: 'Frenzy',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        description: 'Once per long rest: +10 damage, +8 Severe threshold, cannot use Armor Slots until no adversaries in sight.',
      });
      break;

    // Teleport (Codex L5): Once per long rest teleportation
    case 'teleport':
    case 'core_domain_card_teleport':
      tokens.push({
        id: 'teleport-charge',
        name: 'Teleport',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        description: 'Once per long rest. Teleport self + willing Close targets. Spellcast Roll (16) with familiarity modifier.',
      });
      break;

    // Reaper's Strike (Blade L9): Once per long rest attack
    case 'reapers-strike':
    case 'core_domain_card_reapers_strike':
      tokens.push({
        id: 'reapers-strike-charge',
        name: 'Reaper\'s Strike',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        description: 'Once per long rest. Spend 1 Hope, make attack roll, GM reveals valid targets. Pick one for 5 forced HP.',
      });
      break;

    // Copycat (Grace L9): Once per long rest card mimic
    case 'copycat':
    case 'core_domain_card_copycat':
      tokens.push({
        id: 'copycat-charge',
        name: 'Copycat',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        description: 'Once per long rest. Mimic another player\'s card (level ≤8). Hope cost = half card level.',
      });
      break;

    // Transcendent Union (Codex L10): Once per long rest party link
    case 'transcendent-union':
    case 'core_domain_card_transcendent_union':
      tokens.push({
        id: 'transcendent-union-charge',
        name: 'Transcendent Union',
        type: 'token',
        current: 1,
        maximum: 1,
        refreshTrigger: 'long-rest',
        description: 'Once per long rest, 5 Hope. Link 2+ creatures. Linked creatures can redistribute damage/stress.',
      });
      break;

    // Add other cards that need tokens here
    default:
      // No switch case found - tokens array will be empty
      break;
  }

  // SECONDARY CONFIG: Check mechanicsConfig.js TOKEN_DEFINITIONS for additional tokens
  // Used for homebrew tokens and legacy compatibility
  const charForLookup = { loadout: [cardId] };
  const mechanicsConfigTokens = getTokensFromConfig(charForLookup, 'domain-card');
  mechanicsConfigTokens.forEach(configToken => {
    if (!tokens.some(t => t.id === configToken.id)) {
      tokens.push(configToken);
    }
  });

  return tokens;
};

/**
 * Initialize all tokens for a character's domain cards
 * @param {Object} character - Character data
 * @param {string} spellcastTrait - Optional spellcast trait name for accurate token calculations
 * @returns {Object} Updated character with token resources
 */
export const initializeCharacterTokens = (character, spellcastTrait = null) => {
  const updatedCharacter = { ...character };

  if (!updatedCharacter.cardResources) {
    updatedCharacter.cardResources = {};
  }

  // Get all cards (both domainCards and loadout arrays)
  const allCards = [
    ...(character.domainCards || []),
    ...(character.loadout || [])
  ];

  // Remove duplicates
  const uniqueCards = [...new Set(allCards)];

  uniqueCards.forEach(cardId => {
    const tokens = getTokensForCard(cardId, character, spellcastTrait);

    if (tokens.length > 0) {
      if (!updatedCharacter.cardResources[cardId]) {
        updatedCharacter.cardResources[cardId] = {};
      }

      // Clean up any old/incorrect token IDs for this card first
      const existingTokenIds = Object.keys(updatedCharacter.cardResources[cardId]).filter(key =>
        updatedCharacter.cardResources[cardId][key].type === 'token'
      );
      const expectedTokenIds = tokens.map(token => token.id);

      // Remove any token that doesn't match our expected tokens
      existingTokenIds.forEach(existingId => {
        if (!expectedTokenIds.includes(existingId)) {
          delete updatedCharacter.cardResources[cardId][existingId];
        }
      });

      tokens.forEach(token => {
        // Only create if doesn't exist, preserve existing values
        if (!updatedCharacter.cardResources[cardId][token.id]) {
          updatedCharacter.cardResources[cardId][token.id] = token;
        } else {
          // Update properties but preserve current value
          const existing = updatedCharacter.cardResources[cardId][token.id];
          const currentValue = existing.current;
          updatedCharacter.cardResources[cardId][token.id] = {
            ...token,
            current: currentValue // Preserve the current spent/used value
          };
        }
      });
    }
  });

  // Initialize community tokens
  if (character.community) {
    const communityTokens = getTokensForCommunity(character.community, character);

    if (communityTokens.length > 0) {
      const communityKey = `community-${character.community}`;
      if (!updatedCharacter.cardResources[communityKey]) {
        updatedCharacter.cardResources[communityKey] = {};
      }

      // Clean up any old/incorrect community token IDs
      const existingTokenIds = Object.keys(updatedCharacter.cardResources[communityKey]).filter(key =>
        updatedCharacter.cardResources[communityKey][key].type === 'token'
      );
      const expectedTokenIds = communityTokens.map(token => token.id);

      // Remove any token that doesn't match our expected tokens
      existingTokenIds.forEach(existingId => {
        if (!expectedTokenIds.includes(existingId)) {
          delete updatedCharacter.cardResources[communityKey][existingId];
        }
      });

      communityTokens.forEach(token => {
        // Only create if doesn't exist, preserve existing values
        if (!updatedCharacter.cardResources[communityKey][token.id]) {
          updatedCharacter.cardResources[communityKey][token.id] = token;
        } else {
          // Update properties but preserve current value
          const existing = updatedCharacter.cardResources[communityKey][token.id];
          const currentValue = existing.current;
          updatedCharacter.cardResources[communityKey][token.id] = {
            ...token,
            current: currentValue // Preserve the current spent/used value
          };
        }
      });
    }
  }

  // Initialize ancestry tokens
  if (character.ancestry) {
    const ancestryTokens = getTokensForAncestry(character.ancestry, character);

    ancestryTokens.forEach(token => {
      const ancestryKey = `ancestry-${token.ancestryId}`;
      if (!updatedCharacter.cardResources[ancestryKey]) {
        updatedCharacter.cardResources[ancestryKey] = {};
      }

      // Only create if doesn't exist, preserve existing values
      if (!updatedCharacter.cardResources[ancestryKey][token.id]) {
        updatedCharacter.cardResources[ancestryKey][token.id] = token;
      } else {
        // Update properties but preserve current value
        const existing = updatedCharacter.cardResources[ancestryKey][token.id];
        const currentValue = existing.current;
        updatedCharacter.cardResources[ancestryKey][token.id] = {
          ...token,
          current: currentValue // Preserve the current spent/used value
        };
      }
    });
  }

  // Initialize class tokens
  if (character.class) {
    const classTokens = getTokensForClass(character.class, character);

    if (classTokens.length > 0) {
      // Always use lowercase class key for consistency
      const classKey = `class-${character.class.toLowerCase()}`;
      if (!updatedCharacter.cardResources[classKey]) {
        updatedCharacter.cardResources[classKey] = {};
      }

      // Clean up any old/incorrect class token IDs
      const existingTokenIds = Object.keys(updatedCharacter.cardResources[classKey]).filter(key =>
        updatedCharacter.cardResources[classKey][key].type === 'dice' || updatedCharacter.cardResources[classKey][key].type === 'token'
      );
      const expectedTokenIds = classTokens.map(token => token.id);

      // Remove any token that doesn't match our expected tokens
      existingTokenIds.forEach(existingId => {
        if (!expectedTokenIds.includes(existingId)) {
          delete updatedCharacter.cardResources[classKey][existingId];
        }
      });

      classTokens.forEach(token => {
        const existingToken = updatedCharacter.cardResources[classKey][token.id];
        // Only create if doesn't exist, preserve existing values
        if (!existingToken) {
          // DEBUG: Token doesn't exist, creating fresh
          if (token.id === 'channel-raw-power') {
            console.log('[Token Init] Channel Raw Power: Creating fresh token (not found in cardResources)', {
              classKey,
              existingCardResources: JSON.stringify(updatedCharacter.cardResources[classKey]),
              newToken: token
            });
          }
          updatedCharacter.cardResources[classKey][token.id] = token;
        } else {
          // Update properties but preserve current value
          const currentValue = existingToken.current;
          // DEBUG: Token exists, preserving current value
          if (token.id === 'channel-raw-power') {
            console.log('[Token Init] Channel Raw Power: Preserving existing current value', {
              classKey,
              existingCurrent: currentValue,
              existingToken: JSON.stringify(existingToken)
            });
          }
          updatedCharacter.cardResources[classKey][token.id] = {
            ...token,
            current: currentValue // Preserve the current spent/used value
          };
        }
      });
    }
  }

  // Initialize subclass tokens (Phase B: Wordsmith, Syndicate, Divine Wielder, Troubadour, Call of the Brave)
  if (character.subclass) {
    const subclassTokens = getTokensForSubclass(character.subclass, character);

    if (subclassTokens.length > 0) {
      const subclassKey = `subclass-${character.subclass}`;
      if (!updatedCharacter.cardResources[subclassKey]) {
        updatedCharacter.cardResources[subclassKey] = {};
      }

      // Clean up any old/incorrect subclass token IDs
      const existingTokenIds = Object.keys(updatedCharacter.cardResources[subclassKey]).filter(key =>
        updatedCharacter.cardResources[subclassKey][key]?.type === 'token'
      );
      const expectedTokenIds = subclassTokens.map(token => token.id);

      // Remove any token that doesn't match our expected tokens
      existingTokenIds.forEach(existingId => {
        if (!expectedTokenIds.includes(existingId)) {
          delete updatedCharacter.cardResources[subclassKey][existingId];
        }
      });

      subclassTokens.forEach(token => {
        const existingToken = updatedCharacter.cardResources[subclassKey][token.id];
        // Only create if doesn't exist, preserve existing values
        if (!existingToken) {
          updatedCharacter.cardResources[subclassKey][token.id] = token;
        } else {
          // Update properties but preserve current value
          const currentValue = existingToken.current;
          updatedCharacter.cardResources[subclassKey][token.id] = {
            ...token,
            current: currentValue // Preserve the current spent/used value
          };
        }
      });
    }
  }

  return updatedCharacter;
};

/**
 * Update token count for a specific card
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @param {string} tokenId - Token resource ID
 * @param {number} newValue - New token count
 * @returns {Object} Updated character
 */
export const updateTokenCount = (character, cardId, tokenId, newValue) => {
  const updatedCharacter = { ...character };

  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  if (updatedCharacter.cardResources[cardId][tokenId]) {
    updatedCharacter.cardResources[cardId][tokenId].current = Math.max(0, newValue);
  }

  return updatedCharacter;
};

/**
 * Spend tokens for a card (used by dice roller)
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @param {number} amount - Number of tokens to spend
 * @returns {Object} Updated character
 */
export const spendTokens = (character, cardId, amount) => {
  const updatedCharacter = { ...character };
  const cardResources = updatedCharacter.cardResources?.[cardId];

  if (cardResources) {
    // Find first token resource and spend from it
    for (const [tokenId, resource] of Object.entries(cardResources)) {
      if (resource.type === 'token') {
        resource.current = Math.max(0, resource.current - amount);
        break;
      }
    }
  }

  return updatedCharacter;
};

/**
 * Activate Flight tokens via spellcast roll
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} Updated character
 */
export const activateFlightTokens = (character, cardId) => {
  const updatedCharacter = { ...character };
  const cardResources = updatedCharacter.cardResources?.[cardId];

  if (cardResources && cardResources['flight-tokens']) {
    const flightToken = cardResources['flight-tokens'];
    // Set current to maximum (Agility tokens)
    flightToken.current = flightToken.maximum;
  }

  return updatedCharacter;
};

/**
 * Activate Wild Fortress dome (requires Spellcast Roll success)
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateWildFortress = (character) => {
  const updatedCharacter = { ...character };

  // Check if character has enough Hope
  if (updatedCharacter.hope < 2) {
    return {
      success: false,
      character,
      message: '🏰 Not enough Hope to create Wild Fortress (need 2)'
    };
  }

  // Spend 2 Hope
  updatedCharacter.hope = updatedCharacter.hope - 2;

  // Reset fortress HP to 0 (no damage taken yet)
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources['wild-fortress']) updatedCharacter.cardResources['wild-fortress'] = {};

  updatedCharacter.cardResources['wild-fortress']['fortress-hp'] = {
    id: 'fortress-hp',
    name: 'Fortress Hit Points',
    type: 'token',
    current: 0, // No damage taken yet
    maximum: 3,
    refreshTrigger: 'manual',
    requiresActivation: false, // Mark as activated
    thresholds: '15/30'
  };

  return {
    success: true,
    character: updatedCharacter,
    message: '🏰 Wild Fortress created! (0/3 HP marked, Thresholds: 15/30)'
  };
};

/**
 * Activate Thorn Skin by spending Hope
 * @param {Object} character - Character data
 * @param {string} spellcastTraitName - Name of the spellcast trait for this character
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateThornSkin = (character, spellcastTraitName = null) => {
  const updatedCharacter = { ...character };

  // Check if character has Hope to spend
  if (updatedCharacter.hope < 1) {
    return {
      success: false,
      character,
      message: '🌿 Not enough Hope to activate Thorn Skin (need 1)'
    };
  }

  // Spend 1 Hope
  updatedCharacter.hope = updatedCharacter.hope - 1;

  // Get spellcast trait value for token count
  let spellcastValue = 2; // Fallback
  if (spellcastTraitName && character.traits?.[spellcastTraitName]) {
    spellcastValue = character.traits[spellcastTraitName];
  } else {
    spellcastValue = Math.max(
      character.traits?.Presence || 0,
      character.traits?.Instinct || 0,
      character.traits?.Knowledge || 0,
      character.traits?.Finesse || 0,
      2 // minimum fallback
    );
  }

  // Set thorn tokens to spellcast trait value
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources['thorn-skin']) updatedCharacter.cardResources['thorn-skin'] = {};

  updatedCharacter.cardResources['thorn-skin']['thorn-tokens'] = {
    id: 'thorn-tokens',
    name: 'Thorn Tokens',
    type: 'token',
    current: spellcastValue,
    maximum: spellcastValue,
    refreshTrigger: 'rest'
  };

  return {
    success: true,
    character: updatedCharacter,
    message: `🌿 Thorn Skin activated! Gained ${spellcastValue} tokens (spend for damage reduction)`
  };
};

/**
 * Spend Strategic Approach token for +1d6 damage
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const spendStrategicApproach = (character, cardId) => {
  const updatedCharacter = { ...character };

  // Check if tokens are available
  const strategyToken = updatedCharacter.cardResources?.[cardId]?.['strategy-tokens'];
  if (!strategyToken || strategyToken.current < 1) {
    return {
      success: false,
      character,
      message: '📊 No Strategy tokens available (recharges on long rest)'
    };
  }

  // Spend token
  strategyToken.current = strategyToken.current - 1;

  return {
    success: true,
    character: updatedCharacter,
    tokensRemaining: strategyToken.current,
    message: `📊 Strategy token spent! +1d6 to your Knowledge-based attack damage (${strategyToken.current} tokens left)`
  };
};

/**
 * Spend Inspirational Words token to help ally
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const spendInspirationalWords = (character, cardId) => {
  const updatedCharacter = { ...character };

  // Check if tokens are available
  const inspirationToken = updatedCharacter.cardResources?.[cardId]?.['inspiration-tokens'];
  if (!inspirationToken || inspirationToken.current < 1) {
    return {
      success: false,
      character,
      message: '💬 No Inspiration tokens available (recharges on long rest)'
    };
  }

  // Spend token
  inspirationToken.current = inspirationToken.current - 1;

  return {
    success: true,
    character: updatedCharacter,
    tokensRemaining: inspirationToken.current,
    message: `💬 Inspiration token spent! Ally clears 1 Stress or 1 HP, or gains 1 Hope (${inspirationToken.current} tokens left)`
  };
};

/**
 * Spend Hope for Thought Delver surface reading
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const spendHopeForThoughtDelver = (character, cardId) => {
  const updatedCharacter = { ...character };
  const currentHope = character.hope || 0;

  if (currentHope < 1) {
    return {
      success: false,
      character,
      message: '🔮 Not enough Hope (need 1)'
    };
  }

  // Spend Hope
  updatedCharacter.hope = currentHope - 1;

  return {
    success: true,
    character: updatedCharacter,
    message: '🔮 Thought Delver: Reading surface thoughts of target within Far range...'
  };
};

/**
 * Activate Towering Stalk - Utility mode (conjure climbable stalk)
 * Once per rest: Conjure thick stalk up to Far height
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateToweringStalkUtility = (character, cardId) => {
  const updatedCharacter = { ...character };

  // Check if token is available
  const stalkToken = updatedCharacter.cardResources?.[cardId]?.['stalk-utility-uses'];
  if (!stalkToken || stalkToken.current < 1) {
    return {
      success: false,
      character,
      message: '🌿 Towering Stalk (utility) already used (recharges on rest)'
    };
  }

  // Use token
  updatedCharacter.cardResources[cardId]['stalk-utility-uses'].current = 0;

  return {
    success: true,
    character: updatedCharacter,
    message: '🌿 Towering Stalk conjured! Thick, climbable stalk grows up to Far range height.'
  };
};

/**
 * Activate Towering Stalk - Attack mode (erupting stalk)
 * Unlimited uses, costs 1 Stress: Spellcast Roll, d8 physical damage
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateToweringStalkAttack = (character) => {
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;
  const proficiency = character?.proficiency || 1;

  // Check if character has Stress slots available
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      message: '🌿 Cannot use Towering Stalk Attack: No Stress slots available!'
    };
  }

  // Mark Stress
  updatedCharacter.stress = stress + 1;

  return {
    success: true,
    character: updatedCharacter,
    message: `🌿 Towering Stalk Attack! Make Spellcast Roll vs Close targets. ${proficiency}d8 physical damage on success.`
  };
};

/**
 * Activate Signature Move - d20 Hope Die override
 * Roll d20 as Hope Die for your signature combat move. On success, clear 1 Stress.
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateSignatureMove = (character, cardId) => {
  const updatedCharacter = { ...character };

  // Check if token is available
  const sigToken = updatedCharacter.cardResources?.[cardId]?.['signature-move-uses'];
  if (!sigToken || sigToken.current < 1) {
    return {
      success: false,
      character,
      message: '⚔️ Signature Move already used (recharges on rest)'
    };
  }

  // Use token
  updatedCharacter.cardResources[cardId]['signature-move-uses'].current = 0;

  return {
    success: true,
    character: updatedCharacter,
    hopeDieOverride: 'd20',
    message: '⚔️ Signature Move activated! Your next attack rolls d20 as Hope Die. On success, clear 1 Stress!'
  };
};

/**
 * Activate Second Wind post-attack healing
 * Self: Clear 3 Stress OR 1 HP (player chooses)
 * On success with Hope: Ally in Close range also clears 3 Stress OR 1 HP
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @param {boolean} withHope - Whether the attack was a success with Hope (triggers ally healing)
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateSecondWind = (character, cardId, withHope = false) => {
  const updatedCharacter = { ...character };

  // Check if token is available
  const windToken = updatedCharacter.cardResources?.[cardId]?.['second-wind-uses'];
  if (!windToken || windToken.current < 1) {
    return {
      success: false,
      character,
      message: '💨 Second Wind already used (recharges on rest)'
    };
  }

  // Use token
  updatedCharacter.cardResources[cardId]['second-wind-uses'].current = 0;

  if (withHope) {
    return {
      success: true,
      character: updatedCharacter,
      message: '💨 Second Wind (with Hope)! Clear 3 Stress OR 1 HP for yourself AND a Close ally!'
    };
  }

  return {
    success: true,
    character: updatedCharacter,
    message: '💨 Second Wind! Clear 3 Stress OR 1 HP for yourself'
  };
};

/**
 * Activate Healing Field AoE healing
 * Normal mode: Clear 1 HP from self + Close range allies (no cost)
 * Enhanced mode: 2 Hope → Clear 2 HP instead
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @param {string} mode - 'normal' or 'enhanced'
 * @returns {Object} { success: boolean, character: Object, healAmount: number, message: string }
 */
export const activateHealingField = (character, cardId, mode = 'normal') => {
  const updatedCharacter = { ...character };
  const hopeCost = mode === 'enhanced' ? 2 : 0;
  const healAmount = mode === 'enhanced' ? 2 : 1;

  // Check if token is available
  const fieldToken = updatedCharacter.cardResources?.[cardId]?.['healing-field-uses'];
  if (!fieldToken || fieldToken.current < 1) {
    return {
      success: false,
      character,
      healAmount: 0,
      message: '🌿 Healing Field already used (recharges on long rest)'
    };
  }

  // For enhanced mode, check Hope
  if (mode === 'enhanced' && updatedCharacter.hope < hopeCost) {
    return {
      success: false,
      character,
      healAmount: 0,
      message: `🌿 Not enough Hope for enhanced Healing Field (need ${hopeCost})`
    };
  }

  // Use token and spend Hope if enhanced
  updatedCharacter.cardResources[cardId]['healing-field-uses'].current = 0;
  if (mode === 'enhanced') {
    updatedCharacter.hope = updatedCharacter.hope - hopeCost;
  }

  // Apply healing to the character (restore HP - increase towards max)
  const currentHp = updatedCharacter.hp || 0;
  const maxHp = updatedCharacter.maxHp || 8;
  const newHp = Math.min(maxHp, currentHp + healAmount);
  updatedCharacter.hp = newHp;
  const actualHealed = newHp - currentHp;

  const modeLabel = mode === 'enhanced' ? 'Enhanced' : 'Normal';
  return {
    success: true,
    character: updatedCharacter,
    healAmount: actualHealed,
    message: `🌿 ${modeLabel} Healing Field! Restored ${actualHealed} HP (and all Close allies restore ${healAmount} HP)${mode === 'enhanced' ? ' (2 Hope spent)' : ''}`
  };
};

/**
 * Activate Deft Maneuvers sprint ability
 * Mark a Stress to sprint Far range without Agility Roll. +1 attack if ending in Melee.
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} { success: boolean, character: Object, attackBonus: boolean, message: string }
 */
export const activateDeftManeuvers = (character, cardId) => {
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;

  // Check if character has Stress slots available
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      attackBonus: false,
      message: '🏃 Cannot use Deft Maneuvers: No Stress slots available!'
    };
  }

  // Check if token is available
  const deftToken = updatedCharacter.cardResources?.[cardId]?.['deft-maneuvers-uses'];
  if (!deftToken || deftToken.current < 1) {
    return {
      success: false,
      character,
      attackBonus: false,
      message: '🏃 Deft Maneuvers already used (recharges on rest)'
    };
  }

  // Mark Stress and use the token
  updatedCharacter.stress = stress + 1;
  updatedCharacter.cardResources[cardId]['deft-maneuvers-uses'].current = 0;

  return {
    success: true,
    character: updatedCharacter,
    attackBonus: true, // Flag for +1 attack if ending in Melee
    message: '🏃 Deft Maneuvers! Sprint to Far range (no Agility Roll). +1 attack if you end in Melee range!'
  };
};

/**
 * Activate I See It Coming evasion boost (Bone L1 Domain Card)
 * Cost: 1 Stress
 * Effect: Roll d4 and add to Evasion temporarily
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object, activeValue: number, message: string }
 */
export const activateISeeItComing = (character) => {
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;
  const evasion = updatedCharacter.evasion || 0;

  // Check if character has Stress slots available
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      activeValue: null,
      message: '🛡️ Cannot use I See It Coming: No Stress slots available!'
    };
  }

  // Roll d4 for bonus
  const bonus = Math.floor(Math.random() * 4) + 1;
  const boostedEvasion = evasion + bonus;

  // Mark Stress and store bonus in character for vitals bar display
  updatedCharacter.stress = stress + 1;
  updatedCharacter.iSeeItComingBoost = bonus;

  return {
    success: true,
    character: updatedCharacter,
    activeValue: bonus, // The d4 result for active indicator
    message: `🛡️ I See It Coming: +${bonus} (${evasion} → ${boostedEvasion})`
  };
};

/**
 * Clear I See It Coming evasion boost (called when effect expires)
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object }
 */
export const clearISeeItComingBoost = (character) => {
  const updatedCharacter = { ...character };
  delete updatedCharacter.iSeeItComingBoost;
  return {
    success: true,
    character: updatedCharacter
  };
};

/**
 * Activate Notorious roll bonus (Grace L10 Domain Card)
 * Cost: 1 Stress
 * Effect: +10 to next roll
 * Special: This card doesn't count toward loadout limit and cannot be vaulted.
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object, bonusActive: boolean, message: string }
 */
export const activateNotorious = (character) => {
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;

  // Check if character has Stress slots available
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      bonusActive: false,
      message: '⭐ Cannot leverage notoriety: No Stress slots available!'
    };
  }

  // Mark Stress and set bonus flag
  updatedCharacter.stress = stress + 1;

  return {
    success: true,
    character: updatedCharacter,
    bonusActive: true, // Flag for +10 bonus on next roll
    message: '⭐ Notorious! +10 to your next roll!'
  };
};

/**
 * Activate Mending Touch healing spell
 * Normal mode: 2 Hope → heal 1 HP or clear 1 Stress from target
 * Enhanced mode: 2 Hope → heal 2 HP or clear 2 Stress from target (once per long rest, uses token)
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID (either 'mending-touch' or 'core_domain_card_mending_touch')
 * @param {string} mode - 'normal' or 'enhanced'
 * @returns {Object} { success: boolean, character: Object, healAmount: number, message: string }
 */
export const activateMendingTouch = (character, cardId, mode = 'normal') => {
  const updatedCharacter = { ...character };
  const hopeCost = 2;
  const healAmount = mode === 'enhanced' ? 2 : 1;

  // Check if character has enough Hope
  if (updatedCharacter.hope < hopeCost) {
    return {
      success: false,
      character,
      healAmount: 0,
      message: `✨ Not enough Hope to use Mending Touch (need ${hopeCost})`
    };
  }

  // For enhanced mode, check if token is available
  if (mode === 'enhanced') {
    const enhancedToken = updatedCharacter.cardResources?.[cardId]?.['mending-touch-enhanced'];
    if (!enhancedToken || enhancedToken.current < 1) {
      return {
        success: false,
        character,
        healAmount: 0,
        message: '✨ Enhanced Mending Touch already used (recharges on long rest)'
      };
    }

    // Use the enhanced token
    updatedCharacter.cardResources[cardId]['mending-touch-enhanced'].current = 0;
  }

  // Spend Hope
  updatedCharacter.hope = updatedCharacter.hope - hopeCost;

  const modeLabel = mode === 'enhanced' ? 'Enhanced' : 'Normal';
  return {
    success: true,
    character: updatedCharacter,
    healAmount,
    message: `✨ ${modeLabel} Mending Touch! Heal ${healAmount} HP or clear ${healAmount} Stress from target (${hopeCost} Hope spent)`
  };
};

/**
 * Activate Rune Ward to reduce incoming damage
 * Rolls 1d8 for damage reduction. If result is 8, ward depletes until rest.
 * @param {Object} character - Character data
 * @param {number} incomingDamage - Amount of damage being taken (optional, for display purposes)
 * @returns {Object} { success: boolean, character: Object, damageReduction: number, wardDepleted: boolean, message: string }
 */
export const activateRuneWard = (character, incomingDamage = 0) => {
  const updatedCharacter = { ...character };
  const cardId = 'rune-ward';

  // Check if character has Rune Ward in loadout
  if (!hasLoadoutCard(character, 'rune-ward')) {
    return {
      success: false,
      character,
      damageReduction: 0,
      wardDepleted: false,
      message: 'Rune Ward not in loadout'
    };
  }

  // Check if character has Hope to spend
  if (updatedCharacter.hope < 1) {
    return {
      success: false,
      character,
      damageReduction: 0,
      wardDepleted: false,
      message: 'Not enough Hope to activate Rune Ward (need 1)'
    };
  }

  // Check if ward is charged
  const wardCharge = updatedCharacter.cardResources?.[cardId]?.['ward-charge'] ||
                     updatedCharacter.cardResources?.['core_domain_card_rune_ward']?.['ward-charge'];

  if (!wardCharge || wardCharge.current < 1) {
    return {
      success: false,
      character,
      damageReduction: 0,
      wardDepleted: false,
      message: 'Rune Ward is depleted (recharges on rest)'
    };
  }

  // Spend 1 Hope
  updatedCharacter.hope = updatedCharacter.hope - 1;

  // Roll 1d8 for damage reduction
  const wardRoll = Math.ceil(Math.random() * 8);
  const damageReduction = wardRoll;

  // Check if ward depletes (rolls max value of 8)
  const wardDepleted = wardRoll === 8;

  // Initialize cardResources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  // Update ward charge status
  if (wardDepleted) {
    updatedCharacter.cardResources[cardId]['ward-charge'] = {
      id: 'ward-charge',
      name: 'Rune Ward',
      type: 'token',
      current: 0, // Depleted
      maximum: 1,
      refreshTrigger: 'rest',
      requiresActivation: true,
      hopeCost: 1,
      activationText: 'Spend 1 Hope to reduce incoming damage by 1d8',
      description: 'A personal trinket infused with protective magic. Spend Hope to reduce damage by 1d8. If you roll an 8, the ward depletes until your next rest.',
      wardDieSize: 8,
      breakOnMax: true
    };
  }

  // Build message
  let message = `Rune Ward activated! Rolled ${wardRoll} on d8 - reduce damage by ${damageReduction}`;
  if (wardDepleted) {
    message += '. The ward\'s power ends! (Recharge on rest)';
  }

  return {
    success: true,
    character: updatedCharacter,
    damageReduction,
    wardDepleted,
    dieRoll: wardRoll,
    message
  };
};

/**
 * Activate Uncanny Disguise tokens by marking stress
 * @param {Object} character - Character data
 * @param {string} cardId - Domain card ID
 * @returns {Object} Updated character
 */
export const activateDisguiseTokens = (character, cardId) => {
  const updatedCharacter = { ...character };
  const cardResources = updatedCharacter.cardResources?.[cardId];

  if (cardResources && cardResources['disguise-tokens']) {
    const disguiseToken = cardResources['disguise-tokens'];
    // Set current to maximum (spellcast trait tokens)
    disguiseToken.current = disguiseToken.maximum;

    // Mark a stress (increment stress)
    updatedCharacter.stress = (updatedCharacter.stress || 0) + 1;
  }

  return updatedCharacter;
};

/**
 * Check if character is flying (has active Flight tokens) and auto-spend one
 * @param {Object} character - Character data
 * @returns {Object} { isFlying: boolean, updatedCharacter: Object, tokensRemaining: number }
 */
export const handleFlightActionRoll = (character) => {
  const updatedCharacter = { ...character };
  let isFlying = false;
  let tokensRemaining = 0;

  // Check for Flight tokens
  const flightResources = updatedCharacter.cardResources?.['flight'];
  if (flightResources && flightResources['flight-tokens']) {
    const flightTokens = flightResources['flight-tokens'];
    if (flightTokens.current > 0) {
      isFlying = true;
      // Auto-spend one token
      flightTokens.current = Math.max(0, flightTokens.current - 1);
      tokensRemaining = flightTokens.current;
    }
  }

  return {
    isFlying,
    updatedCharacter,
    tokensRemaining
  };
};

/**
 * Check if character is disguised (has active Disguise tokens) and auto-spend one
 * @param {Object} character - Character data
 * @returns {Object} { isDisguised: boolean, updatedCharacter: Object, tokensRemaining: number }
 */
export const handleInvisibilityActionRoll = (character) => {
  const updatedCharacter = { ...character };
  let isInvisible = false;
  let tokensRemaining = 0;

  // Check for Invisibility tokens
  const invisibilityResources = updatedCharacter.cardResources?.['invisibility'];
  if (invisibilityResources && invisibilityResources['invisibility-tokens']) {
    const invisibilityTokens = invisibilityResources['invisibility-tokens'];
    if (invisibilityTokens.current > 0) {
      isInvisible = true;
      // Spend one token for this action
      updatedCharacter.cardResources['invisibility']['invisibility-tokens'].current = invisibilityTokens.current - 1;
      tokensRemaining = invisibilityTokens.current - 1;
    }
  }

  return {
    character: updatedCharacter,
    isInvisible,
    tokensRemaining,
    message: isInvisible
      ? `Invisible action completed! ${tokensRemaining} invisibility tokens remaining.${tokensRemaining === 0 ? ' You are no longer invisible.' : ''}`
      : null
  };
};

export const handleDisguiseActionRoll = (character) => {
  const updatedCharacter = { ...character };
  let isDisguised = false;
  let tokensRemaining = 0;

  // Check for Disguise tokens
  const disguiseResources = updatedCharacter.cardResources?.['uncanny-disguise'];
  if (disguiseResources && disguiseResources['disguise-tokens']) {
    const disguiseTokens = disguiseResources['disguise-tokens'];
    if (disguiseTokens.current > 0) {
      isDisguised = true;
      // Auto-spend one token
      disguiseTokens.current = Math.max(0, disguiseTokens.current - 1);
      tokensRemaining = disguiseTokens.current;
    }
  }

  return {
    isDisguised,
    updatedCharacter,
    tokensRemaining
  };
};

/**
 * Add tide tokens when rolling with Fear (seaborne community feature)
 * @param {Object} character - Character data
 * @returns {Object} Updated character
 */
/**
 * Activate Invisibility spell - triggers spellcast roll and places tokens on success
 * @param {Object} character - Character data
 * @param {string} spellcastTraitName - Name of the spellcast trait for this character
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateInvisibility = (character, spellcastTraitName = null) => {
  // Simulate spellcast roll (DC 10) - roll d12 + spellcast trait
  const rollResult = Math.ceil(Math.random() * 12);

  // Use the provided spellcast trait name, or fallback to common ones
  let spellcastValue = 2; // Fallback
  if (spellcastTraitName && character.traits?.[spellcastTraitName]) {
    spellcastValue = character.traits[spellcastTraitName];
  } else {
    spellcastValue = character.traits?.Presence || character.traits?.Instinct || character.traits?.Knowledge || 2;
  }

  const totalRoll = rollResult + spellcastValue;
  const success = totalRoll >= 10;

  if (!success) {
    return {
      success: false,
      character,
      message: `Spellcast Roll Failed! Rolled ${rollResult} + ${spellcastValue} spellcast = ${totalRoll} (needed 10+)`
    };
  }

  const updatedCharacter = { ...character };

  // Mark a Stress for successful activation
  updatedCharacter.stress = (updatedCharacter.stress || 0) + 1;

  // Get spellcast trait value to determine token count
  const tokens = getTokensForCard('invisibility', character, spellcastTraitName);
  const invisibilityToken = tokens.find(token => token.id === 'invisibility-tokens');

  if (invisibilityToken) {
    // Initialize invisibility tokens to maximum
    if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
    if (!updatedCharacter.cardResources['invisibility']) updatedCharacter.cardResources['invisibility'] = {};

    updatedCharacter.cardResources['invisibility']['invisibility-tokens'] = {
      ...invisibilityToken,
      current: invisibilityToken.maximum
    };
  }

  return {
    success: true,
    character: updatedCharacter,
    message: `Spellcast Success! Rolled ${rollResult} + ${spellcastValue} = ${totalRoll} vs DC 10. Invisibility activated with ${invisibilityToken?.maximum || 0} tokens. Marked 1 Stress.`
  };
};

/**
 * Activate Uncanny Disguise - mark Stress to don a humanoid disguise
 * SRD: Mark Stress to don humanoid disguise. Place tokens = Spellcast trait.
 * Spend 1 token per action while disguised. Advantage on Presence to avoid scrutiny.
 * @param {Object} character - Character data
 * @param {string} cardKey - Card key (default: 'uncanny-disguise')
 * @returns {Object} { success, character, message }
 */
export const activateUncannyDisguise = (character, cardKey = 'uncanny-disguise') => {
  const updatedCharacter = { ...character };

  // Get spellcast trait value for token count (Midnight uses Presence, Instinct, or Knowledge)
  const spellcastValue = Math.max(
    character.traits?.Presence || 0,
    character.traits?.Instinct || 0,
    character.traits?.Knowledge || 0,
    2 // Minimum 2 tokens
  );

  // Mark a Stress to activate
  updatedCharacter.stress = (updatedCharacter.stress || 0) + 1;

  // Initialize card resources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardKey]) updatedCharacter.cardResources[cardKey] = {};

  // Place tokens equal to Spellcast trait
  updatedCharacter.cardResources[cardKey]['disguise-tokens'] = {
    id: 'disguise-tokens',
    name: 'Disguise Tokens',
    type: 'token',
    current: spellcastValue,
    maximum: spellcastValue,
    refreshTrigger: 'manual'
  };

  return {
    success: true,
    character: updatedCharacter,
    message: `🎭 Uncanny Disguise activated! ${spellcastValue} tokens placed. Marked 1 Stress. Advantage on Presence to avoid scrutiny.`
  };
};

/**
 * Handle Minor Illusion success (Sorcerer class feature)
 * SRD: "Make a Spellcast Roll (10). On a success, you create a minor visual illusion
 * no larger than yourself within Close range."
 * @param {Object} character - Character data (not modified for Minor Illusion)
 * @returns {Object} Result object with success and message
 */
export const handleMinorIllusion = (character) => {
  // Minor Illusion has no state changes - just narrative effect
  return {
    success: true,
    character: character, // No changes needed
    message: '🎭 Illusion created! A minor visual illusion appears within Close range.'
  };
};

export const addTideToken = (character) => {
  if (!hasCommunity(character, 'seaborne')) return character;

  const updatedCharacter = { ...character };
  const communityKey = `community-seaborne`;
  const tideTokens = updatedCharacter.cardResources?.[communityKey]?.['tide-tokens'];

  if (tideTokens && tideTokens.current < tideTokens.maximum) {
    tideTokens.current = Math.min(tideTokens.current + 1, tideTokens.maximum);
  }

  return updatedCharacter;
};

/**
 * Refresh tokens based on trigger type
 * @param {Object} character - Character data
 * @param {string} triggerType - 'session', 'beast-feast', etc.
 * @param {string} spellcastTrait - Optional spellcast trait name (from allData.subclasses[subclass].spellcastTrait)
 * @returns {Object} Updated character
 */
export const refreshTokens = (character, triggerType, spellcastTrait = null) => {
  // Deep copy cardResources to avoid mutation issues with React reconciliation
  const updatedCharacter = {
    ...character,
    cardResources: character.cardResources
      ? JSON.parse(JSON.stringify(character.cardResources))
      : {}
  };

  if (updatedCharacter.cardResources) {
    Object.keys(updatedCharacter.cardResources).forEach(cardId => {
      // Get fresh token definitions for this card to handle legacy data
      let freshTokens = [];
      if (cardId.startsWith('community-')) {
        freshTokens = getTokensForCommunity(cardId.replace('community-', ''), updatedCharacter);
      } else if (cardId.startsWith('subclass-')) {
        freshTokens = getTokensForSubclass(cardId.replace('subclass-', ''), updatedCharacter);
      } else if (cardId.startsWith('ancestry-')) {
        freshTokens = getTokensForAncestry(cardId.replace('ancestry-', ''), updatedCharacter);
      } else if (cardId.startsWith('class-')) {
        freshTokens = getTokensForClass(cardId.replace('class-', ''), updatedCharacter);
      } else {
        // Regular domain card - pass spellcastTrait for accurate token calculations
        freshTokens = getTokensForCard(cardId, updatedCharacter, spellcastTrait);
      }

      Object.keys(updatedCharacter.cardResources[cardId]).forEach(tokenId => {
        const token = updatedCharacter.cardResources[cardId][tokenId];

        // Find fresh token definition to handle legacy data missing refreshTrigger
        const freshToken = freshTokens.find(t => t.id === tokenId);

        // Check refresh trigger from EITHER stored token OR fresh definition (legacy data fix)
        const effectiveRefreshTrigger = token.refreshTrigger || freshToken?.refreshTrigger;

        // Determine if this token should refresh based on trigger hierarchy:
        // - 'session' trigger also refreshes 'long-rest' and 'rest' tokens (new session implies a long rest)
        // - 'long-rest' trigger also refreshes 'rest' tokens
        // - 'rest' only refreshes 'rest' tokens
        const shouldRefresh = (token.type === 'token' || token.type === 'dice') && (
          effectiveRefreshTrigger === triggerType ||
          (triggerType === 'session' && (effectiveRefreshTrigger === 'long-rest' || effectiveRefreshTrigger === 'rest')) ||
          (triggerType === 'long-rest' && effectiveRefreshTrigger === 'rest')
        );

        if (shouldRefresh) {
          // Special handling for Seaborne tide tokens - they clear to 0 on new session
          if (tokenId === 'tide-tokens' && triggerType === 'session') {
            token.current = 0; // Tide tokens reset to 0, not maximum
          }
          // Special handling for "-used" tracking tokens (once-per-session spells like Arcane Barrage, Tava's Armor)
          // These track "has this been used?" where current>=1 means "used"
          // On refresh, we want them available again, so set to 0
          else if (tokenId.endsWith('-used')) {
            token.current = 0;
          } else if (freshToken) {
            // Update both maximum and current to reflect new character state
            token.maximum = freshToken.maximum;
            token.current = freshToken.maximum;
            // Also update refreshTrigger in case it was missing (legacy data migration)
            token.refreshTrigger = freshToken.refreshTrigger;
          } else {
            // Fallback to old behavior if fresh token not found
            token.current = token.maximum;
          }
        }
      });
    });
  }

  // Clear pending Channel Raw Power damage bonus on new session
  // Use 0 instead of delete so Firestore update removes the value
  if (triggerType === 'session') {
    updatedCharacter.channelRawPowerDamageBonus = 0;

    // Reset Perfect Recall (School of Knowledge Wizard specialization)
    // Allows first Recall Cost to be reduced by 1 again
    updatedCharacter.perfectRecallUsedThisSession = false;

    // Clear Seraph Prayer Dice at session end/start
    // SRD: "At the end of each session, clear all unspent Prayer Dice"
    if (hasClass(updatedCharacter, 'seraph')) {
      updatedCharacter.prayerDice = [];
    }

    // Clear usedThisSession flags for progressive-die cards (Wild Surge, Zone of Protection)
    // so they can be activated again in the new session
    if (updatedCharacter.cardResources) {
      Object.keys(updatedCharacter.cardResources).forEach(cardId => {
        Object.keys(updatedCharacter.cardResources[cardId]).forEach(tokenId => {
          const token = updatedCharacter.cardResources[cardId][tokenId];
          if (token.type === 'progressive-die') {
            // Clear usedThisSession so it can be activated again
            // Use false instead of delete so Firestore update removes the flag
            token.usedThisSession = false;
            // Reset to fresh state for new session
            token.current = 0;
            token.requiresActivation = true;
          }
        });
      });
    }

    // Clear character-level state for progressive-die cards
    // Use 0 instead of delete so Firestore update removes the value
    updatedCharacter.wildSurgeDie = 0;
    updatedCharacter.zoneProtectionDie = 0;

    // Initialize session-triggered tokens for equipped cards that don't have entries yet
    // This handles cards like Unleash Chaos that need tokens at session start
    if (hasLoadoutCard(updatedCharacter, 'unleash-chaos')) {
      // Calculate max tokens from spellcast trait
      let maxTokens = 2; // Default fallback
      if (spellcastTrait && updatedCharacter.traits?.[spellcastTrait]) {
        maxTokens = updatedCharacter.traits[spellcastTrait];
      }
      maxTokens = Math.max(1, maxTokens);

      // Initialize cardResources if needed
      if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
      if (!updatedCharacter.cardResources['unleash-chaos']) updatedCharacter.cardResources['unleash-chaos'] = {};

      // Set chaos tokens to max (free session refresh)
      updatedCharacter.cardResources['unleash-chaos']['chaos-tokens'] = {
        id: 'chaos-tokens',
        name: 'Chaos Tokens',
        type: 'token',
        current: maxTokens,
        maximum: maxTokens,
        refreshTrigger: 'session'
      };
    }
  }

  // Handle long-rest refresh for layer-defense cards (Confusing Aura)
  // Long rest clears usedThisRest so they can be activated again
  if (triggerType === 'long-rest' || triggerType === 'session') {
    if (updatedCharacter.cardResources) {
      Object.keys(updatedCharacter.cardResources).forEach(cardId => {
        Object.keys(updatedCharacter.cardResources[cardId]).forEach(tokenId => {
          const token = updatedCharacter.cardResources[cardId][tokenId];
          if (token.type === 'layer-defense' && token.refreshTrigger === 'long-rest') {
            // Clear usedThisRest so it can be activated again
            // Use false instead of delete so Firestore update removes the flag
            token.usedThisRest = false;
            // Reset to fresh state
            token.current = 0;
            token.requiresActivation = true;
          }
        });
      });
    }

    // Clear character-level state for layer-defense cards
    // Use 0 instead of delete so Firestore update removes the value
    updatedCharacter.confusingAuraLayers = 0;
  }

  // Clear bonuses that last "until rest" on any rest type
  // Session implies long-rest which implies rest, so clear on any rest type
  if (triggerType === 'rest' || triggerType === 'long-rest' || triggerType === 'session') {
    // Clear No Mercy attack bonus (Warrior Hope feature - lasts until rest)
    if (updatedCharacter.noMercyBonus) {
      updatedCharacter.noMercyBonus = 0;
    }

    // Clear Rogue's Dodge evasion bonus (Rogue Hope feature - lasts until hit or rest)
    if (updatedCharacter.roguesDodgeActive) {
      updatedCharacter.roguesDodgeActive = false;
    }

    // Clear temporary armor (from Tava's Armor, etc.)
    if (updatedCharacter.temporaryArmor?.expiresOn === 'rest') {
      updatedCharacter.temporaryArmor = null;
    }
  }

  return updatedCharacter;
};

/**
 * Activate Never Upstaged when taking HP damage
 * @param {Object} character - Character data
 * @param {number} hpDamage - Amount of HP marked
 * @returns {Object} { activated: boolean, character: Object, message: string }
 */
export const activateNeverUpstaged = (character, hpDamage) => {
  // Check if character has Never Upstaged in loadout
  if (!hasLoadoutCard(character, 'never-upstaged')) {
    return { activated: false, character, message: null };
  }

  const updatedCharacter = { ...character };
  const cardId = 'never-upstaged';

  // Mark a Stress when activating (per card text: "you can mark a Stress to place tokens")
  updatedCharacter.stress = (updatedCharacter.stress || 0) + 1;

  // Initialize resources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  // Get or create upstage tokens
  let upstageTokens = updatedCharacter.cardResources[cardId]['upstage-tokens'];
  if (!upstageTokens) {
    const tokens = getTokensForCard(cardId, character);
    upstageTokens = tokens.find(token => token.id === 'upstage-tokens');
    updatedCharacter.cardResources[cardId]['upstage-tokens'] = { ...upstageTokens, current: 0 };
  }

  // Add tokens equal to HP damage taken
  updatedCharacter.cardResources[cardId]['upstage-tokens'].current += hpDamage;

  return {
    activated: true,
    character: updatedCharacter,
    message: `⚔️ Never Upstaged: Marked 1 Stress, gained ${hpDamage} token${hpDamage !== 1 ? 's' : ''} (auto-apply +5 damage each on next attack)`
  };
};

/**
 * Activate Spellcharge when taking magic HP damage
 * @param {Object} character - Character data
 * @param {number} hpDamage - Amount of HP marked from magic damage
 * @param {string} spellcastTrait - Optional spellcast trait name for this character
 * @returns {Object} { activated: boolean, character: Object, message: string }
 */
export const activateSpellcharge = (character, hpDamage, spellcastTrait = null) => {
  // Check if character has Spellcharge in loadout
  if (!hasLoadoutCard(character, 'spellcharge')) {
    return { activated: false, character, message: null };
  }

  const updatedCharacter = { ...character };
  const cardId = 'spellcharge';

  // Initialize resources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  // Get or create charge tokens with proper spellcast trait maximum
  if (!updatedCharacter.cardResources[cardId]['charge-tokens']) {
    const tokens = getTokensForCard(cardId, character, spellcastTrait);
    const chargeTokenTemplate = tokens.find(token => token.id === 'charge-tokens');
    updatedCharacter.cardResources[cardId]['charge-tokens'] = { ...chargeTokenTemplate, current: 0 };
  }

  // Get reference to the actual tokens object in updatedCharacter
  const chargeTokens = updatedCharacter.cardResources[cardId]['charge-tokens'];

  // Get spellcast trait value for maximum storage
  // Try the provided spellcast trait first, then check maximum, then fallback to common traits
  let spellcastValue;
  if (spellcastTrait && character.traits?.[spellcastTrait]) {
    spellcastValue = character.traits[spellcastTrait];
  } else if (chargeTokens.maximum) {
    spellcastValue = chargeTokens.maximum;
  } else {
    // Check all traits to find the highest one (likely the spellcast trait)
    spellcastValue = Math.max(
      character.traits?.Presence || 0,
      character.traits?.Instinct || 0,
      character.traits?.Knowledge || 0,
      character.traits?.Finesse || 0,
      2 // minimum fallback
    );
  }

  // Card text: "place tokens equal to the number of Hit Points you marked on this card"
  // BUT you can only store up to your spellcast trait total
  const currentTokens = chargeTokens.current || 0;
  const maxTokens = spellcastValue;

  // When taking magic damage, add tokens equal to HP marked, but cap at spellcast trait maximum
  const availableSpace = maxTokens - currentTokens;
  const tokensToAdd = Math.min(hpDamage, availableSpace);

  // Update the tokens directly on the updatedCharacter object
  updatedCharacter.cardResources[cardId]['charge-tokens'] = {
    ...chargeTokens,
    current: currentTokens + tokensToAdd,
    maximum: maxTokens
  };

  // Check if we've reached max tokens and should auto-reset the mode
  const reachedMax = (currentTokens + tokensToAdd) >= maxTokens;
  const shouldReset = reachedMax && tokensToAdd > 0;

  // Prepare appropriate message
  let message;
  if (tokensToAdd === 0 && currentTokens >= maxTokens) {
    message = `🔮 Spellcharge: Already at maximum (${maxTokens}/${maxTokens} tokens)`;
  } else if (tokensToAdd > 0) {
    message = `🔮 Spellcharge: Gained ${tokensToAdd} token${tokensToAdd !== 1 ? 's' : ''} from ${hpDamage} damage (${currentTokens + tokensToAdd}/${maxTokens} stored)`;
    if (reachedMax) {
      message += ' - MAX REACHED';
    }
  } else {
    message = `🔮 Spellcharge: No tokens added (${currentTokens}/${maxTokens} stored)`;
  }

  return {
    activated: true,
    character: updatedCharacter,
    message,
    shouldResetMode: shouldReset
  };
};

/**
 * Check if character has HP-triggered domain cards and return activation options
 * @param {Object} character - Character data
 * @returns {Array} Array of { cardId, name, requiresMagicDamage } for active HP-triggered cards
 */
export const getHPTriggeredCards = (character) => {
  const triggeredCards = [];

  if (hasLoadoutCard(character, 'never-upstaged')) {
    triggeredCards.push({
      cardId: 'never-upstaged',
      name: 'Never Upstaged',
      requiresMagicDamage: false,
      description: 'Gain tokens equal to HP marked, spend for +5 damage each'
    });
  }

  if (hasLoadoutCard(character, 'spellcharge')) {
    triggeredCards.push({
      cardId: 'spellcharge',
      name: 'Spellcharge',
      requiresMagicDamage: true,
      description: 'Gain tokens from magic damage, spend for +d6 damage each'
    });
  }

  return triggeredCards;
};

/**
 * Activate Faerie Luckbender - reroll Duality Dice for self or ally
 * @param {Object} character - Character data
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateLuckbender = (character) => {
  const updatedCharacter = { ...character };

  // Check if character is a Faerie
  if (!hasAncestryFeature(character, 'Luckbender')) {
    return {
      success: false,
      character,
      message: '🍀 Luckbender is only available to characters with the Luckbender feature'
    };
  }

  // Check if character has enough Hope
  if (updatedCharacter.hope < 3) {
    return {
      success: false,
      character,
      message: '🍀 Not enough Hope to activate Luckbender (need 3)'
    };
  }

  // Check if Luckbender use is available
  const ancestryKey = `ancestry-faerie`;
  const luckbenderUses = updatedCharacter.cardResources?.[ancestryKey]?.['luckbender-uses'];

  if (!luckbenderUses || luckbenderUses.current < 1) {
    return {
      success: false,
      character,
      message: '🍀 Luckbender already used this session'
    };
  }

  // Spend 3 Hope
  updatedCharacter.hope = updatedCharacter.hope - 3;

  // Spend the Luckbender use
  updatedCharacter.cardResources[ancestryKey]['luckbender-uses'].current = 0;

  return {
    success: true,
    character: updatedCharacter,
    message: '🍀 Luckbender activated! Rerolling Duality Dice... (spent 3 Hope)'
  };
};

/**
 * Activate/Deactivate Galapa Retract - Shell defensive mode
 * SRD: "Mark a Stress to retract into your shell. While in your shell, you have
 * resistance to physical damage, you have disadvantage on action rolls, and you can't move."
 * @param {Object} character - Character data
 * @param {boolean} retract - True to enter shell, false to exit
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateRetract = (character, retract = true) => {
  const updatedCharacter = { ...character };

  // Check if character is Galapa
  if (!hasAncestryFeature(character, 'Retract')) {
    return {
      success: false,
      character,
      message: 'Retract is only available to characters with the Retract feature'
    };
  }

  if (retract) {
    // Entering shell - requires marking Stress
    const stress = character.stress || 0;
    const maxStress = character.maxStress || 8;

    if (stress >= maxStress) {
      return {
        success: false,
        character,
        message: 'Cannot retract: No Stress slots available'
      };
    }

    // Already retracted?
    if (character.isRetracted) {
      return {
        success: false,
        character,
        message: 'Already retracted into shell'
      };
    }

    // Mark stress and enter shell
    updatedCharacter.stress = stress + 1;
    updatedCharacter.isRetracted = true;

    return {
      success: true,
      character: updatedCharacter,
      message: 'Retracted into shell! Physical damage halved, disadvantage on actions, cannot move.'
    };
  } else {
    // Exiting shell - free action
    updatedCharacter.isRetracted = false;

    return {
      success: true,
      character: updatedCharacter,
      message: 'Emerged from shell.'
    };
  }
};

/**
 * Activate Rally - Bard rallies the party and creates a Rally Die
 * @param {Object} character - Character data
 * @returns {Object} {success: boolean, character: Object, message: string}
 */
export const activateRally = (character) => {
  const updatedCharacter = { ...character };

  // Check if character is a bard
  if (updatedCharacter.class !== 'bard') {
    return {
      success: false,
      character,
      message: '🎵 Rally is only available to Bards'
    };
  }

  // Check if Rally Die already exists this session
  const classKey = `class-bard`;
  const rallyDie = updatedCharacter.cardResources?.[classKey]?.['rally-die'];

  if (!rallyDie) {
    return {
      success: false,
      character,
      message: '🎵 Rally Die not initialized'
    };
  }

  if (rallyDie.current > 0) {
    return {
      success: false,
      character,
      message: '🎵 Rally already used this session'
    };
  }

  // Create Rally Die (set current to 1)
  updatedCharacter.cardResources[classKey]['rally-die'].current = 1;

  const dieSize = rallyDie.dieSize;

  return {
    success: true,
    character: updatedCharacter,
    message: `🎵 Rally activated! You and your allies gain a d${dieSize} Rally Die`
  };
};

/**
 * Spend Rally Die - Roll the die and add to action/reaction/damage or clear Stress
 * @param {Object} character - Character data
 * @param {string} useType - 'roll' or 'stress'
 * @returns {Object} {success: boolean, character: Object, dieRoll: number, message: string}
 */
export const spendRallyDie = (character, useType = 'roll') => {
  const updatedCharacter = { ...character };

  // Check if character is a bard
  if (updatedCharacter.class !== 'bard') {
    return {
      success: false,
      character,
      dieRoll: 0,
      message: '🎵 Rally Die is only available to Bards'
    };
  }

  // Check if Rally Die exists and is available
  const classKey = `class-bard`;
  const rallyDie = updatedCharacter.cardResources?.[classKey]?.['rally-die'];

  if (!rallyDie || rallyDie.current < 1) {
    return {
      success: false,
      character,
      dieRoll: 0,
      message: '🎵 No Rally Die available (use Rally ability first)'
    };
  }

  // Spend the Rally Die
  updatedCharacter.cardResources[classKey]['rally-die'].current = 0;

  // Maestro: Give to ally (no roll needed - effect handled by caller)
  if (useType === 'ally') {
    return {
      success: true,
      character: updatedCharacter,
      dieRoll: 0,
      message: '🎵 Rally Die given to ally!'
    };
  }

  // Roll the Rally Die (for self-use cases)
  const dieRoll = Math.ceil(Math.random() * rallyDie.dieSize);

  let message;
  if (useType === 'stress') {
    // Clear Stress equal to roll
    const stressCleared = Math.min(dieRoll, updatedCharacter.stress || 0);
    updatedCharacter.stress = (updatedCharacter.stress || 0) - stressCleared;
    message = `🎵 Rally Die spent! Rolled ${dieRoll}, cleared ${stressCleared} Stress`;
  } else {
    // Add to roll (roll, damage, etc.)
    message = `🎵 Rally Die spent! Rolled ${dieRoll}, add to your roll`;
  }

  return {
    success: true,
    character: updatedCharacter,
    dieRoll,
    message
  };
};

/**
 * Activate Channel Raw Power - Sorcerer sacrifices a domain card for Hope or damage bonus
 * @param {Object} character - Character data
 * @param {string} cardId - ID of domain card to sacrifice
 * @param {string} choice - 'hope' or 'damage'
 * @param {number} cardLevel - Level of the card being sacrificed
 * @param {Object} allData - All game data for card lookups
 * @returns {Object} {success: boolean, character: Object, damageBonus: number, message: string}
 */
export const activateChannelRawPower = (character, cardId, choice, cardLevel, allData) => {
  const updatedCharacter = { ...character };

  // Check if character is a sorcerer
  if (updatedCharacter.class !== 'sorcerer') {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: '⚡ Channel Raw Power is only available to Sorcerers'
    };
  }

  // Check if ability is available (not used this long rest)
  const classKey = 'class-sorcerer';
  const channelToken = updatedCharacter.cardResources?.[classKey]?.['channel-raw-power'];

  if (!channelToken || channelToken.current < 1) {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: '⚡ Channel Raw Power already used (refreshes on long rest)'
    };
  }

  // Check if card is in loadout
  if (!updatedCharacter.loadout?.includes(cardId)) {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: '⚡ Card not in loadout'
    };
  }

  // Move card from loadout to vault
  updatedCharacter.loadout = updatedCharacter.loadout.filter(id => id !== cardId);
  if (!updatedCharacter.vault) updatedCharacter.vault = [];
  updatedCharacter.vault = [...updatedCharacter.vault, cardId];

  // Mark ability as used
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[classKey]) updatedCharacter.cardResources[classKey] = {};
  updatedCharacter.cardResources[classKey]['channel-raw-power'] = {
    ...channelToken,
    current: 0
  };

  // Get card name for message
  const card = allData?.domainCards?.[cardId];
  const cardName = card?.name || cardId;

  let damageBonus = 0;
  let message;

  if (choice === 'hope') {
    // Gain Hope equal to card level
    updatedCharacter.hope = (updatedCharacter.hope || 0) + cardLevel;
    // Clear any pending damage bonus (use 0 so Firestore update removes the value)
    updatedCharacter.channelRawPowerDamageBonus = 0;
    message = `⚡ Channel Raw Power! Sacrificed ${cardName} (L${cardLevel}) → gained ${cardLevel} Hope`;
  } else if (choice === 'damage') {
    // Damage bonus = 2× card level (returned for caller to store in LOCAL state, NOT on character)
    // This prevents stale closure issues from Firebase sync
    damageBonus = cardLevel * 2;
    // NOTE: We intentionally do NOT store on character object - caller uses setChannelRawPowerBonus(damageBonus)
    message = `⚡ Channel Raw Power! Sacrificed ${cardName} (L${cardLevel}) → +${damageBonus} damage on next spell`;
  } else {
    // Invalid choice - shouldn't happen, but don't set any bonus
    message = `⚡ Channel Raw Power! Sacrificed ${cardName} (L${cardLevel})`;
  }

  return {
    success: true,
    character: updatedCharacter,
    damageBonus,
    message
  };
};

/**
 * Check if Channel Raw Power damage bonus is pending
 * @param {Object} character - Character data
 * @returns {number} Pending damage bonus (0 if none)
 */
export const getChannelRawPowerDamageBonus = (character) => {
  return character?.channelRawPowerDamageBonus || 0;
};

/**
 * Consume Channel Raw Power damage bonus (call after applying to spell damage)
 * @param {Object} character - Character data
 * @returns {Object} Updated character with bonus cleared
 */
export const consumeChannelRawPowerDamageBonus = (character) => {
  const updatedCharacter = { ...character };
  // Use 0 instead of delete so Firestore update removes the value
  updatedCharacter.channelRawPowerDamageBonus = 0;
  return updatedCharacter;
};

/**
 * Activate Forest Sprites - Summon protective sprites by spending Hope
 * Requires successful Spellcast Roll (13), then spend X Hope to create X sprites
 * @param {Object} character - Character data
 * @param {number} hopeToSpend - Number of Hope to spend (1 Hope = 1 sprite)
 * @returns {Object} { success: boolean, character: Object, spritesCreated: number, message: string }
 */
export const activateForestSprites = (character, hopeToSpend) => {
  const updatedCharacter = { ...character };
  const cardId = 'forest-sprites';

  // Check if character has Forest Sprites in loadout
  if (!hasLoadoutCard(character, 'forest-sprites')) {
    return {
      success: false,
      character,
      spritesCreated: 0,
      message: 'Forest Sprites not in loadout'
    };
  }

  // Validate Hope to spend
  if (!hopeToSpend || hopeToSpend < 1) {
    return {
      success: false,
      character,
      spritesCreated: 0,
      message: 'Must spend at least 1 Hope to create sprites'
    };
  }

  // Check if character has enough Hope
  if ((updatedCharacter.hope || 0) < hopeToSpend) {
    return {
      success: false,
      character,
      spritesCreated: 0,
      message: `Not enough Hope (have ${updatedCharacter.hope || 0}, need ${hopeToSpend})`
    };
  }

  // Spend Hope
  updatedCharacter.hope = (updatedCharacter.hope || 0) - hopeToSpend;

  // Initialize cardResources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  // Get existing sprite count and add new sprites
  const existingSprites = updatedCharacter.cardResources[cardId]['active-sprites']?.current || 0;
  const newSpriteCount = existingSprites + hopeToSpend;

  // Update sprite tokens
  updatedCharacter.cardResources[cardId]['active-sprites'] = {
    id: 'active-sprites',
    name: 'Forest Sprites',
    type: 'token',
    current: newSpriteCount,
    maximum: 99,
    refreshTrigger: 'manual',
    requiresActivation: false, // Now activated
    description: 'Small forest sprites appear at points within Far range. Allies attacking adversaries in Melee range of a sprite gain +3 to attack. Allies marking Armor Slots in Melee range of a sprite mark an additional slot. Sprites vanish after granting a benefit or taking any damage.',
    benefitsProvided: {
      attackBonus: { value: 3, condition: 'Ally attacks adversary within Melee range of a sprite' },
      armorBonus: { value: 1, condition: 'Ally marks Armor Slot while within Melee range of a sprite' }
    }
  };

  return {
    success: true,
    character: updatedCharacter,
    spritesCreated: hopeToSpend,
    totalSprites: newSpriteCount,
    message: `Forest Sprites summoned! Created ${hopeToSpend} sprite${hopeToSpend !== 1 ? 's' : ''} (${newSpriteCount} total). Allies gain +3 attack near sprites, +1 Armor Slot when marking near sprites.`
  };
};

/**
 * Consume a Forest Sprite when it grants a benefit or takes damage
 * @param {Object} character - Character data
 * @param {string} reason - Why the sprite was consumed ('attack_bonus', 'armor_bonus', 'damage')
 * @returns {Object} { success: boolean, character: Object, spritesRemaining: number, message: string }
 */
export const consumeForestSprite = (character, reason = 'benefit') => {
  const updatedCharacter = { ...character };
  const cardId = 'forest-sprites';

  // Check for active sprites
  const spriteToken = updatedCharacter.cardResources?.[cardId]?.['active-sprites'];
  if (!spriteToken || spriteToken.current < 1) {
    return {
      success: false,
      character,
      spritesRemaining: 0,
      message: 'No active Forest Sprites to consume'
    };
  }

  // Consume one sprite
  updatedCharacter.cardResources[cardId]['active-sprites'].current = spriteToken.current - 1;
  const remaining = spriteToken.current - 1;

  // Generate appropriate message based on reason
  let reasonText;
  switch (reason) {
    case 'attack_bonus':
      reasonText = 'granted +3 attack bonus';
      break;
    case 'armor_bonus':
      reasonText = 'granted extra Armor Slot';
      break;
    case 'damage':
      reasonText = 'took damage';
      break;
    default:
      reasonText = 'granted a benefit';
  }

  return {
    success: true,
    character: updatedCharacter,
    spritesRemaining: remaining,
    message: `Forest Sprite vanished (${reasonText}). ${remaining} sprite${remaining !== 1 ? 's' : ''} remaining.`
  };
};

/**
 * Get the number of active Forest Sprites
 * @param {Object} character - Character data
 * @returns {number} Number of active sprites
 */
export const getActiveForestSprites = (character) => {
  return character?.cardResources?.['forest-sprites']?.['active-sprites']?.current || 0;
};

/**
 * Check if Arcana-Touched is active (4+ Arcana domain cards in loadout)
 * @param {Object} character - Character data
 * @param {Object} allData - All game data containing domain cards
 * @returns {Object} { isActive: boolean, arcanaCardCount: number, passiveBonus: number }
 */
export const checkArcanaTouchedActive = (character, allData) => {
  const loadout = character?.loadout || [];
  const domainCards = allData?.domainCards || {};

  // Count Arcana domain cards in loadout
  let arcanaCardCount = 0;
  loadout.forEach(cardId => {
    const card = domainCards[cardId];
    if (card?.domain === 'ARCANA') {
      arcanaCardCount++;
    }
  });

  const isActive = arcanaCardCount >= 4;

  return {
    isActive,
    arcanaCardCount,
    passiveBonus: isActive ? 1 : 0, // +1 to Spellcast Rolls when active
    message: isActive
      ? `Arcana-Touched active (${arcanaCardCount}/4 Arcana cards): +1 Spellcast`
      : `Arcana-Touched inactive (${arcanaCardCount}/4 Arcana cards needed)`
  };
};

/**
 * Activate Arcana-Touched dice switch - swap Hope and Fear dice results
 * @param {Object} character - Character data
 * @param {Object} allData - All game data for domain card lookups
 * @param {number} hopeDieResult - Original Hope die result
 * @param {number} fearDieResult - Original Fear die result
 * @returns {Object} { success: boolean, character: Object, newHopeDie: number, newFearDie: number, message: string }
 */
export const activateArcanaTouchedSwitch = (character, allData, hopeDieResult, fearDieResult) => {
  const updatedCharacter = { ...character };

  // Check if Arcana-Touched is in loadout
  if (!hasLoadoutCard(character, 'arcana-touched')) {
    return {
      success: false,
      character,
      newHopeDie: hopeDieResult,
      newFearDie: fearDieResult,
      message: 'Arcana-Touched not in loadout'
    };
  }

  // Check if Arcana-Touched is active (4+ Arcana cards)
  const { isActive, arcanaCardCount } = checkArcanaTouchedActive(character, allData);
  if (!isActive) {
    return {
      success: false,
      character,
      newHopeDie: hopeDieResult,
      newFearDie: fearDieResult,
      message: `Arcana-Touched requires 4+ Arcana cards (currently ${arcanaCardCount})`
    };
  }

  // Use consistent card ID for token lookup
  const cardId = 'arcana-touched';

  // Check if ability is available (not used this rest)
  const switchToken = updatedCharacter.cardResources?.[cardId]?.['arcana-touched-switch'];

  if (!switchToken || switchToken.current < 1) {
    return {
      success: false,
      character,
      newHopeDie: hopeDieResult,
      newFearDie: fearDieResult,
      message: 'Arcana-Touched dice switch already used this rest'
    };
  }

  // Spend the token
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['arcana-touched-switch'] = {
    ...switchToken,
    current: 0
  };

  // Swap the dice results
  const newHopeDie = fearDieResult;
  const newFearDie = hopeDieResult;

  return {
    success: true,
    character: updatedCharacter,
    newHopeDie,
    newFearDie,
    originalHopeDie: hopeDieResult,
    originalFearDie: fearDieResult,
    message: `Arcana-Touched! Dice switched: Hope ${hopeDieResult}→${newHopeDie}, Fear ${fearDieResult}→${newFearDie}`
  };
};

/**
 * Check if Midnight-Touched is active (4+ Midnight domain cards in loadout)
 * @param {Object} character - Character data
 * @param {Object} allData - All game data containing domain cards
 * @returns {Object} { isActive: boolean, midnightCardCount: number }
 */
export const checkMidnightTouchedActive = (character, allData) => {
  const loadout = character?.loadout || [];
  const domainCards = allData?.domainCards || {};

  // Count Midnight domain cards in loadout
  let midnightCardCount = 0;
  loadout.forEach(cardId => {
    const card = domainCards[cardId];
    if (card?.domain === 'MIDNIGHT') {
      midnightCardCount++;
    }
  });

  const isActive = midnightCardCount >= 4;

  return {
    isActive,
    midnightCardCount,
    message: isActive
      ? `Midnight-Touched active (${midnightCardCount}/4 Midnight cards)`
      : `Midnight-Touched inactive (${midnightCardCount}/4 Midnight cards needed)`
  };
};

/**
 * Activate Midnight-Touched Hope Steal - Gain Hope instead of GM gaining Fear
 * Requires: 0 Hope, GM would gain Fear, once per rest
 * @param {Object} character - Character data
 * @param {Object} allData - All game data for domain card lookups
 * @returns {Object} { success: boolean, character: Object, message: string }
 */
export const activateMidnightHopeSteal = (character, allData) => {
  const updatedCharacter = { ...character };

  // Check if Midnight-Touched is in loadout
  if (!hasLoadoutCard(character, 'midnight-touched')) {
    return {
      success: false,
      character,
      message: 'Midnight-Touched not in loadout'
    };
  }

  // Check if Midnight-Touched is active (4+ Midnight cards)
  const { isActive, midnightCardCount } = checkMidnightTouchedActive(character, allData);
  if (!isActive) {
    return {
      success: false,
      character,
      message: `Midnight-Touched requires 4+ Midnight cards (currently ${midnightCardCount})`
    };
  }

  // Check if character has 0 Hope (required condition)
  if ((updatedCharacter.hope || 0) > 0) {
    return {
      success: false,
      character,
      message: 'Midnight-Touched Hope Steal requires 0 Hope'
    };
  }

  // Use consistent card ID for token lookup
  const cardId = 'midnight-touched';

  // Check if ability is available (not used this rest)
  const hopeStealToken = updatedCharacter.cardResources?.[cardId]?.['midnight-hope-steal'];

  if (!hopeStealToken || hopeStealToken.current < 1) {
    return {
      success: false,
      character,
      message: 'Midnight-Touched Hope Steal already used this rest'
    };
  }

  // Spend the token
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['midnight-hope-steal'] = {
    ...hopeStealToken,
    current: 0
  };

  // Gain 1 Hope (instead of GM gaining Fear)
  updatedCharacter.hope = 1;

  return {
    success: true,
    character: updatedCharacter,
    message: 'Midnight-Touched! Stole the darkness - gained 1 Hope instead of GM gaining Fear'
  };
};

/**
 * Activate Midnight-Touched Fear Damage - Add Fear Die result to damage
 * Requires: Successful attack, costs 1 Stress, unlimited uses
 * @param {Object} character - Character data
 * @param {Object} allData - All game data for domain card lookups
 * @param {number} fearDieResult - The Fear die result from the attack roll
 * @returns {Object} { success: boolean, character: Object, damageBonus: number, message: string }
 */
export const activateMidnightFearDamage = (character, allData, fearDieResult) => {
  const updatedCharacter = { ...character };

  // Check if Midnight-Touched is in loadout
  if (!hasLoadoutCard(character, 'midnight-touched')) {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: 'Midnight-Touched not in loadout'
    };
  }

  // Check if Midnight-Touched is active (4+ Midnight cards)
  const { isActive, midnightCardCount } = checkMidnightTouchedActive(character, allData);
  if (!isActive) {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: `Midnight-Touched requires 4+ Midnight cards (currently ${midnightCardCount})`
    };
  }

  // Check if character has Stress to spend
  const currentStress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;
  if (currentStress >= maxStress) {
    return {
      success: false,
      character,
      damageBonus: 0,
      message: 'Cannot activate Midnight-Touched Fear Damage: No Stress slots available'
    };
  }

  // Mark 1 Stress
  updatedCharacter.stress = currentStress + 1;

  // Damage bonus is the Fear die result
  const damageBonus = fearDieResult;

  return {
    success: true,
    character: updatedCharacter,
    damageBonus,
    message: `Midnight-Touched! Channeled fear into power - +${damageBonus} damage (marked 1 Stress)`
  };
};

/**
 * Check if Sage-Touched is active (4+ Sage domain cards in loadout)
 * @param {Object} character - Character data
 * @param {Object} allData - All game data containing domain cards
 * @returns {Object} { isActive: boolean, sageCardCount: number, passiveBonus: number, inNaturalEnvironment: boolean }
 */
export const checkSageTouchedActive = (character, allData, inNaturalEnvironment = false) => {
  const loadout = character?.loadout || [];
  const domainCards = allData?.domainCards || {};

  // Count Sage domain cards in loadout
  let sageCardCount = 0;
  loadout.forEach(cardId => {
    const card = domainCards[cardId];
    if (card?.domain === 'SAGE') {
      sageCardCount++;
    }
  });

  const isActive = sageCardCount >= 4;

  // Passive bonus only applies in natural environments
  const passiveBonus = (isActive && inNaturalEnvironment) ? 2 : 0;

  return {
    isActive,
    sageCardCount,
    passiveBonus, // +2 to Spellcast Rolls when active AND in natural environment
    inNaturalEnvironment,
    message: isActive
      ? inNaturalEnvironment
        ? `Sage-Touched active (${sageCardCount}/4 Sage cards): +2 Spellcast in natural environment`
        : `Sage-Touched active (${sageCardCount}/4 Sage cards): +2 Spellcast only in natural environments`
      : `Sage-Touched inactive (${sageCardCount}/4 Sage cards needed)`
  };
};

/**
 * Activate Sage-Touched double trait ability
 * Once per rest, double Agility or Instinct for a roll (must choose before rolling)
 * @param {Object} character - Character data
 * @param {Object} allData - All game data for domain card lookups
 * @param {string} traitToDouble - 'Agility' or 'Instinct'
 * @returns {Object} { success: boolean, character: Object, originalValue: number, doubledValue: number, message: string }
 */
export const activateSageTouchedDoubleTrait = (character, allData, traitToDouble) => {
  const updatedCharacter = { ...character };

  // Validate trait choice
  if (!['Agility', 'Instinct'].includes(traitToDouble)) {
    return {
      success: false,
      character,
      originalValue: 0,
      doubledValue: 0,
      message: 'Sage-Touched can only double Agility or Instinct'
    };
  }

  // Check if Sage-Touched is in loadout
  if (!hasLoadoutCard(character, 'sage-touched')) {
    return {
      success: false,
      character,
      originalValue: 0,
      doubledValue: 0,
      message: 'Sage-Touched not in loadout'
    };
  }

  // Check if Sage-Touched is active (4+ Sage cards)
  const { isActive, sageCardCount } = checkSageTouchedActive(character, allData);
  if (!isActive) {
    return {
      success: false,
      character,
      originalValue: 0,
      doubledValue: 0,
      message: `Sage-Touched requires 4+ Sage cards (currently ${sageCardCount})`
    };
  }

  // Use consistent card ID for token lookup
  const cardId = 'sage-touched';

  // Check if ability is available (not used this rest)
  const doubleToken = updatedCharacter.cardResources?.[cardId]?.['sage-touched-double-trait'];

  if (!doubleToken || doubleToken.current < 1) {
    return {
      success: false,
      character,
      originalValue: 0,
      doubledValue: 0,
      message: 'Sage-Touched double trait already used this rest'
    };
  }

  // Get the trait value to double
  const originalValue = character.traits?.[traitToDouble] || 0;
  const doubledValue = originalValue * 2;

  // Spend the token
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['sage-touched-double-trait'] = {
    ...doubleToken,
    current: 0
  };

  return {
    success: true,
    character: updatedCharacter,
    trait: traitToDouble,
    originalValue,
    doubledValue,
    message: `Sage-Touched! ${traitToDouble} doubled: ${originalValue} → ${doubledValue} for this roll`
  };
};

// ============================================================================
// WILD SURGE (Sage L7)
// Progressive die that enhances all action rolls
// ============================================================================

/**
 * Activate Wild Surge - mark Stress, set die to 1
 * @param {Object} character - Character object
 * @returns {Object} Result with updated character
 */
export const activateWildSurge = (character) => {
  const cardId = 'wild-surge';
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;

  // Check if already active
  const existingDie = character.cardResources?.[cardId]?.['wild-surge-die'];
  if (existingDie && existingDie.current > 0) {
    return {
      success: false,
      character,
      message: '🌿 Wild Surge is already active!'
    };
  }

  // Check Stress availability
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      message: '🌿 Cannot activate Wild Surge: No Stress slots available!'
    };
  }

  // Mark Stress
  updatedCharacter.stress = stress + 1;

  // Set die to 1 (active)
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['wild-surge-die'] = {
    id: 'wild-surge-die',
    name: 'Wild Surge Die',
    type: 'progressive-die',
    current: 1, // Start at 1
    maximum: 6,
    dieSize: 6,
    refreshTrigger: 'long-rest',
    requiresActivation: false // Now active
  };

  // Also store in character state for easy DiceRoller access
  updatedCharacter.wildSurgeDie = 1;

  return {
    success: true,
    character: updatedCharacter,
    dieValue: 1,
    message: '🌿 Wild Surge activated! +1 to all action rolls. Die will increase after each roll.'
  };
};

/**
 * Increment Wild Surge die after an action roll
 * @param {Object} character - Character object
 * @returns {Object} Result with updated character (or ended state if die exceeds 6)
 */
export const incrementWildSurgeDie = (character) => {
  const cardId = 'wild-surge';
  const updatedCharacter = { ...character };

  const currentDie = character.cardResources?.[cardId]?.['wild-surge-die']?.current || 0;

  if (currentDie === 0) {
    return {
      success: false,
      character,
      message: 'Wild Surge is not active'
    };
  }

  const newValue = currentDie + 1;

  // Check if die would exceed 6
  if (newValue > 6) {
    // End the surge and mark extra Stress
    return endWildSurge(updatedCharacter);
  }

  // Update the die value
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['wild-surge-die'] = {
    ...character.cardResources[cardId]['wild-surge-die'],
    current: newValue
  };

  updatedCharacter.wildSurgeDie = newValue;

  return {
    success: true,
    character: updatedCharacter,
    previousValue: currentDie,
    newValue: newValue,
    message: `🌿 Wild Surge Die: ${currentDie} → ${newValue}`
  };
};

/**
 * End Wild Surge (when die exceeds 6 or on rest)
 * @param {Object} character - Character object
 * @returns {Object} Result with updated character
 */
export const endWildSurge = (character) => {
  const cardId = 'wild-surge';
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;

  // Mark extra Stress for ending
  if (stress < maxStress) {
    updatedCharacter.stress = stress + 1;
  }

  // Reset the die to 0 (inactive)
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['wild-surge-die'] = {
    id: 'wild-surge-die',
    name: 'Wild Surge Die',
    type: 'progressive-die',
    current: 0, // Inactive
    maximum: 6,
    dieSize: 6,
    refreshTrigger: 'long-rest',
    usedThisSession: true // Can't reactivate until session refresh
  };

  // Clear character state (use 0 so Firestore update persists the change)
  updatedCharacter.wildSurgeDie = 0;

  return {
    success: true,
    character: updatedCharacter,
    ended: true,
    message: '🌿 Wild Surge ends! Your form drops and you mark a Stress.'
  };
};

/**
 * Get current Wild Surge die value for DiceRoller integration
 * @param {Object} character - Character object
 * @returns {number} Current die value (0 if inactive)
 */
export const getWildSurgeDieValue = (character) => {
  return character.wildSurgeDie ||
         character.cardResources?.['wild-surge']?.['wild-surge-die']?.current ||
         0;
};

/**
 * Check if Wild Surge is active
 * @param {Object} character - Character object
 * @returns {boolean} True if Wild Surge is active
 */
export const isWildSurgeActive = (character) => {
  return getWildSurgeDieValue(character) > 0;
};

// ============================================================================
// ZONE OF PROTECTION (Splendor L6)
// Progressive die that reduces ally damage in zone
// ============================================================================

/**
 * Get Zone of Protection die value
 * Checks both direct state and both possible cardKey formats
 * @param {Object} character - Character object
 * @returns {number} Current die value (0 if inactive)
 */
const getZoneProtectionDieValueInternal = (character) => {
  // First check direct state (most reliable)
  if (character.zoneProtectionDie) {
    return character.zoneProtectionDie;
  }

  // Check both possible cardKey formats in cardResources
  const resources = character.cardResources || {};
  const shortKey = resources['zone-of-protection']?.['protection-die']?.current;
  const longKey = resources['core_domain_card_zone_of_protection']?.['protection-die']?.current;

  return shortKey || longKey || 0;
};

/**
 * Find the storage key for Zone of Protection in cardResources
 * @param {Object} character - Character object
 * @returns {string} The storage key being used, or default
 */
const findZoneProtectionStorageKey = (character) => {
  const resources = character.cardResources || {};
  if (resources['core_domain_card_zone_of_protection']?.['protection-die']) {
    return 'core_domain_card_zone_of_protection';
  }
  return 'zone-of-protection';
};

/**
 * Activate Zone of Protection - auto-rolls Spellcast (DC 16)
 * @param {Object} character - Character object
 * @param {string} cardKey - Card key (for consistent cardResources storage)
 * @param {string} spellcastTraitName - Optional spellcast trait name
 * @returns {Object} Result with updated character
 */
export const activateZoneOfProtection = (character, cardKey = 'zone-of-protection', spellcastTraitName = null) => {
  const cardId = 'zone-of-protection';
  const storageKey = cardKey || cardId; // Use provided cardKey for storage
  const updatedCharacter = { ...character };

  // Check if already active
  const existingDie = character.cardResources?.[storageKey]?.['protection-die'];
  if (existingDie && existingDie.current > 0) {
    return {
      success: false,
      character,
      message: '✨ Zone of Protection is already active!'
    };
  }

  // Check if already used this session
  if (existingDie?.usedThisSession) {
    return {
      success: false,
      character,
      message: 'Zone of Protection already used this session (once per long rest).'
    };
  }

  // Auto-roll Spellcast (DC 16) - roll 2d12 (Hope + Fear) + spellcast trait
  const hopeDie = Math.ceil(Math.random() * 12);
  const fearDie = Math.ceil(Math.random() * 12);
  const diceTotal = hopeDie + fearDie;

  // Subclass to spellcast trait mapping (from subclasses.json)
  const SUBCLASS_SPELLCAST_TRAITS = {
    'troubadour': 'Presence',
    'wordsmith': 'Presence',
    'warden-of-the-elements': 'Instinct',
    'warden-of-renewal': 'Instinct',
    'beastbound': 'Agility',
    'wayfinder': 'Agility',
    'nightwalker': 'Finesse',
    'syndicate': 'Finesse',
    'divine-wielder': 'Strength',
    'winged-sentinel': 'Strength',
    'elemental-origin': 'Instinct',
    'primal-origin': 'Instinct',
    'school-of-knowledge': 'Knowledge',
    'school-of-war': 'Knowledge',
  };

  // Determine spellcast trait from subclass or provided trait name
  let spellcastTraitFromSubclass = null;
  if (character.subclass) {
    spellcastTraitFromSubclass = SUBCLASS_SPELLCAST_TRAITS[character.subclass];
  }

  // Get spellcast trait value
  let spellcastValue = 0;
  let usedTrait = 'none';

  if (spellcastTraitName && character.traits?.[spellcastTraitName]) {
    spellcastValue = character.traits[spellcastTraitName];
    usedTrait = spellcastTraitName;
  } else if (spellcastTraitFromSubclass && character.traits?.[spellcastTraitFromSubclass]) {
    spellcastValue = character.traits[spellcastTraitFromSubclass];
    usedTrait = spellcastTraitFromSubclass;
  } else {
    // Fallback: try common spellcast traits
    const traits = character.traits || {};
    if (traits.Presence) { spellcastValue = traits.Presence; usedTrait = 'Presence'; }
    else if (traits.Instinct) { spellcastValue = traits.Instinct; usedTrait = 'Instinct'; }
    else if (traits.Knowledge) { spellcastValue = traits.Knowledge; usedTrait = 'Knowledge'; }
    else { spellcastValue = 0; usedTrait = 'none'; }
  }

  const totalRoll = diceTotal + spellcastValue;
  const DC = 16;
  const isSuccess = totalRoll >= DC;

  // Determine if it's a critical (doubles)
  const isCritical = hopeDie === fearDie;
  const criticalText = isCritical ? (isSuccess ? ' CRITICAL SUCCESS!' : ' (Doubles!)') : '';

  // Format trait display
  const traitDisplay = spellcastValue !== 0 ? `${spellcastValue} ${usedTrait}` : '0';

  if (!isSuccess) {
    // Failed roll - can retry (doesn't count as "used")
    return {
      success: false,
      character, // Return original character unchanged
      hopeDie,
      fearDie,
      spellcastValue,
      usedTrait,
      totalRoll,
      message: `🎲 Spellcast Failed! (${hopeDie} + ${fearDie}) + ${traitDisplay} = ${totalRoll} vs DC ${DC}.${criticalText} Try again!`
    };
  }

  // Success - set die to 1 (active)
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[storageKey]) updatedCharacter.cardResources[storageKey] = {};

  updatedCharacter.cardResources[storageKey]['protection-die'] = {
    id: 'protection-die',
    name: 'Zone Protection Die',
    type: 'progressive-die',
    current: 1, // Start at 1
    maximum: 6,
    dieSize: 6,
    refreshTrigger: 'long-rest',
    requiresActivation: false // Now active
  };

  // Store for easy access
  updatedCharacter.zoneProtectionDie = 1;

  return {
    success: true,
    character: updatedCharacter,
    dieValue: 1,
    hopeDie,
    fearDie,
    spellcastValue,
    usedTrait,
    totalRoll,
    message: `🎲 Spellcast Success! (${hopeDie} + ${fearDie}) + ${traitDisplay} = ${totalRoll} vs DC ${DC}.${criticalText} ✨ Zone created! Allies reduce damage by 1.`
  };
};

/**
 * Use Zone of Protection to reduce damage
 * @param {Object} character - Character object
 * @param {number} incomingDamage - Damage to reduce
 * @returns {Object} Result with updated character and reduced damage
 */
export const useZoneOfProtection = (character, incomingDamage) => {
  const updatedCharacter = { ...character };

  // Get current die value from character state or cardResources
  const currentDie = getZoneProtectionDieValueInternal(character);

  // Find the storage key being used
  const cardId = findZoneProtectionStorageKey(character);

  if (currentDie === 0) {
    return {
      success: false,
      character,
      damageReduction: 0,
      finalDamage: incomingDamage,
      message: 'Zone of Protection is not active'
    };
  }

  const damageReduction = currentDie;
  const finalDamage = Math.max(0, incomingDamage - damageReduction);
  const newValue = currentDie + 1;

  // Check if die would exceed 6
  if (newValue > 6) {
    // End the zone
    if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
    if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

    updatedCharacter.cardResources[cardId]['protection-die'] = {
      id: 'protection-die',
      name: 'Zone Protection Die',
      type: 'progressive-die',
      current: 0, // Inactive
      maximum: 6,
      dieSize: 6,
      refreshTrigger: 'long-rest',
      requiresActivation: true, // Needs reactivation
      usedThisSession: true // Can't reactivate until session refresh
    };

    // Clear character state (use 0 so Firestore update persists the change)
    updatedCharacter.zoneProtectionDie = 0;

    return {
      success: true,
      character: updatedCharacter,
      damageReduction,
      finalDamage,
      ended: true, // Signal to ProgressiveDiePattern that the zone ended
      newValue: 0, // Die reset to 0
      message: `✨ Zone reduced damage by ${damageReduction}! (${incomingDamage} → ${finalDamage}). Zone of Protection ends!`
    };
  }

  // Update the die value
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  updatedCharacter.cardResources[cardId]['protection-die'] = {
    ...character.cardResources[cardId]['protection-die'],
    current: newValue
  };

  updatedCharacter.zoneProtectionDie = newValue;

  return {
    success: true,
    character: updatedCharacter,
    damageReduction,
    finalDamage,
    previousDie: currentDie,
    newValue, // Used by ProgressiveDiePattern to update session state
    message: `✨ Zone reduced damage by ${damageReduction}! (${incomingDamage} → ${finalDamage}). Die: ${currentDie} → ${newValue}`
  };
};

/**
 * Check if Zone of Protection is active
 * @param {Object} character - Character object
 * @returns {boolean} True if Zone is active
 */
export const isZoneOfProtectionActive = (character) => {
  return getZoneProtectionDieValueInternal(character) > 0;
};

/**
 * Get Zone of Protection die value (exported version)
 * @param {Object} character - Character object
 * @returns {number} Current die value (0 if inactive)
 */
export const getZoneProtectionDieValue = (character) => {
  return getZoneProtectionDieValueInternal(character);
};

// =============================================================================
// CONFUSING AURA (Arcana L8) - Layer-based defense with Spellcast activation
// =============================================================================

/**
 * Subclass to spellcast trait mapping (shared with Zone of Protection)
 */
const SUBCLASS_SPELLCAST_TRAITS = {
  'troubadour': 'Presence',
  'wordsmith': 'Presence',
  'warden-of-the-elements': 'Instinct',
  'warden-of-renewal': 'Instinct',
  'beastbound': 'Agility',
  'wayfinder': 'Agility',
  'nightwalker': 'Finesse',
  'syndicate': 'Finesse',
  'divine-wielder': 'Strength',
  'winged-sentinel': 'Strength',
  'elemental-origin': 'Instinct',
  'primal-origin': 'Instinct',
  'school-of-knowledge': 'Knowledge',
  'school-of-war': 'Knowledge',
};

/**
 * Activate Confusing Aura with auto-roll Spellcast (DC 14)
 * @param {Object} character - Character object
 * @param {string} cardKey - Card key for storage
 * @param {number} extraLayers - Number of extra layers (costs 1 Stress each)
 * @returns {Object} Result with updated character
 */
export const activateConfusingAura = (character, cardKey = 'confusing-aura', extraLayers = 0) => {
  const storageKey = cardKey;
  const updatedCharacter = { ...character };

  // Check if already active
  const existingLayers = character.cardResources?.[storageKey]?.['aura-layers']?.current;
  if (existingLayers && existingLayers > 0) {
    return {
      success: false,
      character,
      message: '✨ Confusing Aura is already active!'
    };
  }

  // Check if already used this long rest
  if (character.cardResources?.[storageKey]?.['aura-layers']?.usedThisRest) {
    return {
      success: false,
      character,
      message: 'Confusing Aura already used this long rest.'
    };
  }

  // Check Stress availability for extra layers
  const stress = character.stress || 0;
  const maxStress = character.maxStress || 8;
  if (extraLayers > 0 && stress + extraLayers > maxStress) {
    return {
      success: false,
      character,
      message: `Not enough Stress slots for ${extraLayers} extra layers!`
    };
  }

  // Auto-roll Spellcast (DC 14) - roll 2d12 (Hope + Fear) + spellcast trait
  const hopeDie = Math.ceil(Math.random() * 12);
  const fearDie = Math.ceil(Math.random() * 12);
  const diceTotal = hopeDie + fearDie;

  // Determine spellcast trait from subclass
  let spellcastTraitFromSubclass = null;
  if (character.subclass) {
    spellcastTraitFromSubclass = SUBCLASS_SPELLCAST_TRAITS[character.subclass];
  }

  // Get spellcast trait value
  let spellcastValue = 0;
  let usedTrait = 'none';

  if (spellcastTraitFromSubclass && character.traits?.[spellcastTraitFromSubclass]) {
    spellcastValue = character.traits[spellcastTraitFromSubclass];
    usedTrait = spellcastTraitFromSubclass;
  } else {
    // Fallback: try common spellcast traits
    const traits = character.traits || {};
    if (traits.Presence) { spellcastValue = traits.Presence; usedTrait = 'Presence'; }
    else if (traits.Instinct) { spellcastValue = traits.Instinct; usedTrait = 'Instinct'; }
    else if (traits.Knowledge) { spellcastValue = traits.Knowledge; usedTrait = 'Knowledge'; }
  }

  const totalRoll = diceTotal + spellcastValue;
  const DC = 14;
  const isSuccess = totalRoll >= DC;

  // Determine if it's a critical (doubles)
  const isCritical = hopeDie === fearDie;
  const criticalText = isCritical ? (isSuccess ? ' CRITICAL!' : ' (Doubles!)') : '';

  // Format trait display
  const traitDisplay = spellcastValue !== 0 ? `${spellcastValue >= 0 ? '+' : ''}${spellcastValue} ${usedTrait}` : '+0';

  if (!isSuccess) {
    // Failed roll - can retry (doesn't count as "used")
    return {
      success: false,
      character, // Return original character unchanged
      message: `🎲 Spellcast Failed!${criticalText} (${hopeDie} + ${fearDie}) ${traitDisplay} = ${totalRoll} vs DC ${DC}. Try again!`
    };
  }

  // Success! Mark Stress for extra layers
  if (extraLayers > 0) {
    updatedCharacter.stress = stress + extraLayers;
  }

  const totalLayers = 1 + extraLayers; // 1 base + extras

  // Set up aura layers
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[storageKey]) updatedCharacter.cardResources[storageKey] = {};

  updatedCharacter.cardResources[storageKey]['aura-layers'] = {
    id: 'aura-layers',
    name: 'Aura Layers',
    type: 'layer-defense',
    current: totalLayers,
    maximum: 99, // No real max
    refreshTrigger: 'long-rest',
    requiresActivation: false, // Now active
    usedThisRest: true // Can't reactivate until long rest
  };

  // Also store for easy access
  updatedCharacter.confusingAuraLayers = totalLayers;

  const stressText = extraLayers > 0 ? ` (${extraLayers} Stress for extra layers)` : '';

  return {
    success: true,
    character: updatedCharacter,
    layers: totalLayers,
    message: `🎲 Spellcast Success!${criticalText} (${hopeDie} + ${fearDie}) ${traitDisplay} = ${totalRoll} vs DC ${DC}. Confusing Aura activated with ${totalLayers} layer${totalLayers > 1 ? 's' : ''}!${stressText}`
  };
};

/**
 * Get current Confusing Aura layer count
 * @param {Object} character - Character object
 * @returns {number} Current layer count (0 if inactive)
 */
export const getConfusingAuraLayers = (character) => {
  // Check character-level state first
  if (character.confusingAuraLayers && character.confusingAuraLayers > 0) {
    return character.confusingAuraLayers;
  }

  // Check cardResources
  const keys = ['confusing-aura', 'core_domain_card_confusing_aura'];
  for (const key of keys) {
    const layers = character.cardResources?.[key]?.['aura-layers']?.current;
    if (layers && layers > 0) {
      return layers;
    }
  }

  return 0;
};

/**
 * Check if Confusing Aura is active
 * @param {Object} character - Character object
 * @returns {boolean} True if Confusing Aura has layers
 */
export const isConfusingAuraActive = (character) => {
  return getConfusingAuraLayers(character) > 0;
};

/**
 * Check if Confusing Aura was used this rest
 * @param {Object} character - Character object
 * @returns {boolean} True if used
 */
export const isConfusingAuraUsedThisRest = (character) => {
  const keys = ['confusing-aura', 'core_domain_card_confusing_aura'];
  for (const key of keys) {
    if (character.cardResources?.[key]?.['aura-layers']?.usedThisRest) {
      return true;
    }
  }
  return false;
};

/**
 * End Confusing Aura (manual or when all dice fail)
 * @param {Object} character - Character object
 * @param {string} cardKey - Card key for storage
 * @returns {Object} Result with updated character
 */
export const endConfusingAura = (character, cardKey = 'confusing-aura') => {
  const updatedCharacter = { ...character };

  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardKey]) updatedCharacter.cardResources[cardKey] = {};

  updatedCharacter.cardResources[cardKey]['aura-layers'] = {
    id: 'aura-layers',
    name: 'Aura Layers',
    type: 'layer-defense',
    current: 0,
    maximum: 99,
    refreshTrigger: 'long-rest',
    requiresActivation: true,
    usedThisRest: true // Still counts as used
  };

  // Clear character state (use 0 so Firestore update persists the change)
  updatedCharacter.confusingAuraLayers = 0;

  return {
    success: true,
    character: updatedCharacter,
    message: 'Confusing Aura dismissed.'
  };
};

// ============================================================================
// UNLEASH CHAOS HANDLERS
// ============================================================================

/**
 * Replenish Unleash Chaos tokens by marking Stress
 * SRD: "Mark a Stress to replenish this card with tokens (up to your Spellcast trait)."
 * @param {Object} character - Character object
 * @param {string} cardKey - Card key for storage
 * @param {string} spellcastTraitName - Name of spellcast trait
 * @returns {Object} Result with updated character
 */
export const replenishUnleashChaos = (character, cardKey = 'unleash-chaos', spellcastTraitName = null) => {
  const updatedCharacter = { ...character };
  const stress = updatedCharacter.stress || 0;
  const maxStress = updatedCharacter.maxStress || 8;

  // Check if can mark Stress
  if (stress >= maxStress) {
    return {
      success: false,
      character,
      message: 'No Stress slots available to replenish Chaos tokens.'
    };
  }

  // Get spellcast trait value for max tokens
  let maxTokens = 2;
  if (spellcastTraitName && character.traits?.[spellcastTraitName]) {
    maxTokens = character.traits[spellcastTraitName];
  }
  maxTokens = Math.max(1, maxTokens);

  // Initialize card resources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardKey]) updatedCharacter.cardResources[cardKey] = {};

  const currentTokens = updatedCharacter.cardResources[cardKey]['chaos-tokens']?.current || 0;

  // Check if already at max
  if (currentTokens >= maxTokens) {
    return {
      success: false,
      character,
      message: `Chaos tokens already at maximum (${maxTokens}).`
    };
  }

  // Mark Stress
  updatedCharacter.stress = stress + 1;

  // Replenish tokens to max
  updatedCharacter.cardResources[cardKey]['chaos-tokens'] = {
    id: 'chaos-tokens',
    name: 'Chaos Tokens',
    type: 'token',
    current: maxTokens,
    maximum: maxTokens,
    refreshTrigger: 'session'
  };

  return {
    success: true,
    character: updatedCharacter,
    tokensReplenished: maxTokens - currentTokens,
    message: `⚡ Marked 1 Stress to replenish ${maxTokens - currentTokens} Chaos tokens!`
  };
};

/**
 * Activate Conjured Steeds - Summon magical steeds by spending variable Hope
 * SRD: Spend any number of Hope to conjure that many magical steeds
 * Last until long rest or steeds take damage
 * @param {Object} character - Character data
 * @param {number} hopeToSpend - Number of Hope to spend (1 Hope = 1 steed)
 * @returns {Object} { success: boolean, character: Object, message: string, activeValue: number }
 */
export const activateConjuredSteeds = (character, hopeToSpend = 1) => {
  const updatedCharacter = { ...character };
  const cardId = 'conjured-steeds';

  // Validate Hope to spend
  if (!hopeToSpend || hopeToSpend < 1) {
    return {
      success: false,
      character,
      message: 'Must spend at least 1 Hope to conjure steeds'
    };
  }

  // Check if character has enough Hope
  const currentHope = updatedCharacter.hope || 0;
  if (currentHope < hopeToSpend) {
    return {
      success: false,
      character,
      message: `Not enough Hope (have ${currentHope}, need ${hopeToSpend})`
    };
  }

  // Spend Hope
  updatedCharacter.hope = currentHope - hopeToSpend;

  // Initialize cardResources if needed
  if (!updatedCharacter.cardResources) updatedCharacter.cardResources = {};
  if (!updatedCharacter.cardResources[cardId]) updatedCharacter.cardResources[cardId] = {};

  // Store active steeds count
  updatedCharacter.cardResources[cardId]['active-steeds'] = {
    id: 'active-steeds',
    name: 'Conjured Steeds',
    type: 'token',
    current: hopeToSpend,
    maximum: 99,
    refreshTrigger: 'long-rest', // Cleared at long rest
    description: `${hopeToSpend} magical steed${hopeToSpend !== 1 ? 's' : ''} conjured. Double land speed. Far range movement without rolls in danger. Riders: -2 attack, +2 damage.`
  };

  return {
    success: true,
    character: updatedCharacter,
    activeValue: hopeToSpend, // Used for display in button label
    message: `🐎 Conjured ${hopeToSpend} magical steed${hopeToSpend !== 1 ? 's' : ''}! Double land speed, Far range movement in danger. Riders: -2 attack, +2 damage.`
  };
};

/**
 * rollUnyieldingArmor - Roll Proficiency d6s for Unyielding Armor
 * SRD: "When you would mark an Armor Slot, roll a number of d6s equal to your Proficiency.
 * If any roll a 6, reduce the severity by one threshold without marking an Armor Slot."
 *
 * @param {Object} character - Character object
 * @returns {Object} { success: boolean, character: Object, message: string, rolls: number[], hasSuccess: boolean }
 */
export const rollUnyieldingArmor = (character) => {
  const proficiency = character?.proficiency || 1;

  // Roll proficiency d6s
  const rolls = [];
  for (let i = 0; i < proficiency; i++) {
    rolls.push(Math.floor(Math.random() * 6) + 1);
  }

  // Check if any roll is a 6
  const hasSuccess = rolls.includes(6);
  const countSixes = rolls.filter(r => r === 6).length;

  // Format rolls display
  const rollDisplay = rolls.map(r => r === 6 ? `**${r}**` : r).join(', ');

  if (hasSuccess) {
    return {
      success: true,
      character,
      rolls,
      hasSuccess: true,
      message: `🛡️ Unyielding Armor SUCCESS! Rolled [${rollDisplay}] - ${countSixes} six${countSixes > 1 ? 'es' : ''}! Reduce severity by one threshold without marking armor.`
    };
  } else {
    return {
      success: true,
      character,
      rolls,
      hasSuccess: false,
      message: `🛡️ Unyielding Armor: Rolled [${rollDisplay}] - No sixes. Mark armor slot as normal.`
    };
  }
};